import { v4 } from 'uuid';

function base64ToBytes(base64) {
    const binString = atob(base64);
    return Uint8Array.from(binString, (m)=>m.codePointAt(0));
}
function bytesToBase64(bytes) {
    const binString = Array.from(bytes, (byte)=>String.fromCodePoint(byte)).join('');
    return btoa(binString);
}
const base64Decode = (data)=>{
    return new TextDecoder().decode(base64ToBytes(data));
};
const base64Encode = (s)=>{
    return bytesToBase64(new TextEncoder().encode(s));
};

function _object_without_properties$2(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$2(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$2(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * ## API
 *
 * The Apple Pay SDK returns a pascalCase `ApplePayPayment` object, which we convert to snake_case
 * for consistency with the rest of the SDK's public APIs.
 *
 * The Apple Pay SDK `onpaymentauthorized` callback is called with an `ApplePayPaymentAuthorizedEvent`,
 * which contains a `payment` property of type `ApplePayPayment`.
 * Historically, we only cared about the `payment.token.payment_data` property, which is "An object
 * containing the encrypted payment data"
 * However, we now also want the `payment.billingContact` and `payment.paymentMethod`
 *
 * For backwards compatability, we accept a `payment.token.payment_data` object, but we also accept
 * the full `payment` object.
 *
 * The full `payment` object simplifies the caller's code, as they don't need to extract specific
 * properties, and it also future-proofs the API if we need more fields in the future
 *
 * ## Types
 *
 * Types are copied from @types/applepayjs
 * https://github.com/DefinitelyTyped/DefinitelyTyped/blob/9fec6d5e5c2d9c03bf0e412b336880b7a9257c9f/types/applepayjs/index.d.ts#L423
 * , stripped of any fields we don't care about (e.g. `shippingContact`), then converted from
 * pascalCase to snake_case to match the rest of the SDK's public APIs.
 *
 * The exception is the `ApplePayPaymentData` type, which is an existing type in the SDK.
 * The applepayjs SDK represents this as `any` - i.e. an opaque type - but we actually parse/split
 * it out
 */ /**
 * Map of Apple Pay `supportedNetworks` to our bank card types
 *
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916122-supportednetworks
 */ const NETWORK_TO_BANK_CARD_TYPE = {
    amex: 'AMEX',
    visa: 'VISA',
    mastercard: 'MASTERCARD',
    discover: 'DISCOVER'
};
const mapAddress = (contact)=>{
    var _contact_address_lines, _contact, _contact_address_lines1, _contact1, _contact2, _contact3, _contact4, _contact5, _contact6;
    return {
        line1: ((_contact = contact) === null || _contact === void 0 ? void 0 : (_contact_address_lines = _contact.address_lines) === null || _contact_address_lines === void 0 ? void 0 : _contact_address_lines[0]) || '',
        line2: (_contact1 = contact) === null || _contact1 === void 0 ? void 0 : (_contact_address_lines1 = _contact1.address_lines) === null || _contact_address_lines1 === void 0 ? void 0 : _contact_address_lines1[1],
        locality: ((_contact2 = contact) === null || _contact2 === void 0 ? void 0 : _contact2.locality) || '',
        administrative_area: ((_contact3 = contact) === null || _contact3 === void 0 ? void 0 : _contact3.administrative_area) || ((_contact4 = contact) === null || _contact4 === void 0 ? void 0 : _contact4.sub_administrative_area) || '',
        postal_code: ((_contact5 = contact) === null || _contact5 === void 0 ? void 0 : _contact5.postal_code) || '',
        country: ((_contact6 = contact) === null || _contact6 === void 0 ? void 0 : _contact6.country_code) || ''
    };
};
// NB: Although the Apple Pay documentation uses camelCase for network names, manual testing shows this is not the case
// when generating payment tokens (e.g. "Visa" instead of expected "visa"), so using lowercase to simplify mapping
const mapApplePayBankCardType = (network)=>NETWORK_TO_BANK_CARD_TYPE[network.toLowerCase()];
const isApplePayPayment = (data)=>'token' in data;
const tokenizePlainApplePay = (sessionData, paymentData, settings)=>{
    var _settings;
    const tender = {
        customer_id: sessionData.customerId,
        customer_type: sessionData.customerType,
        session_id: sessionData.sessionId,
        email: sessionData.email,
        token: {
            tender_type: 'MP',
            tender_product_type: 'AP',
            bank_card_type: mapApplePayBankCardType(paymentData.token.payment_method.network),
            tender_token: {
                transaction_identifier: paymentData.token.transaction_identifier,
                payment_data: paymentData.token.payment_data,
                payment_method: paymentData.token.payment_method
            }
        }
    };
    if ((_settings = settings) === null || _settings === void 0 ? void 0 : _settings.paymentProcessorOverride) {
        tender.token.processor_type = settings.paymentProcessorOverride;
        tender.token.processor_override = true;
    }
    const encodedTender = base64Encode(JSON.stringify(tender));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return encodedTender;
};
const tokenizeApplePay = (sessionData, options, settings)=>{
    var _applePayPayment_token, _applePayPayment, _settings, _applePayPayment_billing_contact, _applePayPayment1, _applePayPayment_billing_contact1, _applePayPayment2, _applePayPayment_billing_contact2, _applePayPayment3, _applePayPayment_billing_contact3, _applePayPayment4, _applePayPayment5;
    const defaultApplePayOptions = options;
    const { email } = defaultApplePayOptions, applePayData = _object_without_properties$2(defaultApplePayOptions, [
        "email"
    ]);
    const applePayPayment = isApplePayPayment(applePayData) ? applePayData : undefined;
    const tokenData = isApplePayPayment(applePayData) ? applePayData.token.payment_data : applePayData;
    const { version, data, signature, header: { ephemeral_public_key, public_key_hash, transaction_id } } = tokenData;
    const { decrypt_alias, application_data } = defaultApplePayOptions;
    // In case of KNET Apple Pay integration it requires different approach in payload tokenization
    if (((_applePayPayment = applePayPayment) === null || _applePayPayment === void 0 ? void 0 : (_applePayPayment_token = _applePayPayment.token) === null || _applePayPayment_token === void 0 ? void 0 : _applePayPayment_token.transaction_identifier) && ((_settings = settings) === null || _settings === void 0 ? void 0 : _settings.paymentProcessorOverride) === 'KNET') {
        return tokenizePlainApplePay(sessionData, applePayPayment, settings);
    }
    const tender = {
        customer_id: sessionData.customerId,
        customer_type: sessionData.customerType,
        session_id: sessionData.sessionId,
        email: sessionData.email || email || '',
        token: {
            bank_card_type: applePayPayment ? mapApplePayBankCardType(applePayPayment.token.payment_method.network) : undefined,
            tender_type: 'MP',
            tender_product_type: 'AP',
            tender_token: {
                version,
                data,
                signature,
                decrypt_alias,
                application_data,
                header: {
                    ephemeral_public_key,
                    public_key_hash,
                    transaction_id
                }
            }
        },
        name: ((_applePayPayment1 = applePayPayment) === null || _applePayPayment1 === void 0 ? void 0 : (_applePayPayment_billing_contact = _applePayPayment1.billing_contact) === null || _applePayPayment_billing_contact === void 0 ? void 0 : _applePayPayment_billing_contact.given_name) || ((_applePayPayment2 = applePayPayment) === null || _applePayPayment2 === void 0 ? void 0 : (_applePayPayment_billing_contact1 = _applePayPayment2.billing_contact) === null || _applePayPayment_billing_contact1 === void 0 ? void 0 : _applePayPayment_billing_contact1.family_name) ? `${((_applePayPayment3 = applePayPayment) === null || _applePayPayment3 === void 0 ? void 0 : (_applePayPayment_billing_contact2 = _applePayPayment3.billing_contact) === null || _applePayPayment_billing_contact2 === void 0 ? void 0 : _applePayPayment_billing_contact2.given_name) || ''} ${((_applePayPayment4 = applePayPayment) === null || _applePayPayment4 === void 0 ? void 0 : (_applePayPayment_billing_contact3 = _applePayPayment4.billing_contact) === null || _applePayPayment_billing_contact3 === void 0 ? void 0 : _applePayPayment_billing_contact3.family_name) || ''}` : undefined,
        address: ((_applePayPayment5 = applePayPayment) === null || _applePayPayment5 === void 0 ? void 0 : _applePayPayment5.billing_contact) ? mapAddress(applePayPayment.billing_contact) : undefined
    };
    const encodedTender = base64Encode(JSON.stringify(tender));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return encodedTender;
};

const tokenizeGiftCard = (customerId, customerType, sessionId, email, giftCardData, processorId = '')=>{
    const { card_number, security_code } = giftCardData;
    if (!card_number) {
        throw new Error('card_number is a required input for tokenizeGiftCard method');
    }
    return base64Encode(JSON.stringify({
        customer_id: customerId,
        customer_type: customerType,
        session_id: sessionId,
        email,
        token: {
            tender_type: 'GC',
            tender_product_type: 'GC',
            card_number,
            security_code,
            tender_token: {
                protection_source: 'NO',
                processor_source: processorId
            }
        }
    }));
};

const productTypeMap = {
    VISA: 'VS',
    MASTERCARD: 'MC',
    DISCOVER: 'DV',
    AMEX: 'AX',
    PREPAID: 'GC',
    GIFT_CARD: 'GC',
    GIFTCARD: 'GC',
    VAULTED_ACCOUNT: 'VA',
    APPLE_PAY: 'AP',
    ANDRIOD_PAY: 'AD',
    PAYPAL: 'PA',
    TOKEN: 'TO',
    NONCE: 'NO',
    VCO: 'VC',
    VENMO: 'VE',
    AMAZON_PAY: 'AZ',
    VIPPS: 'VI',
    FLEET: 'FL'
};
const tokenizeSavedPayment = (customerId, customerType, sessionId, email, savedPayment)=>{
    const { payment_method_id, default: is_default, name, card_type, last_four, requires_security_code_by, processor_tender_id, processor_type, security_code, processor_customer_id, postal_code, expiry_month, expiry_year } = savedPayment;
    if (!payment_method_id) {
        throw new Error('payment_method_id is a required input for tokenizeSavedPayment');
    }
    if (!card_type) {
        throw new Error(`card_type should be one of: AMEX, VISA, MASTERCARD, DISCOVER, DINERS_CLUB, PREPAID`);
    }
    const tenderProductType = productTypeMap[card_type.toUpperCase()];
    return base64Encode(JSON.stringify({
        customer_id: customerId,
        customer_type: customerType,
        session_id: sessionId,
        email,
        token: {
            tender_type: tenderProductType === 'GC' ? 'GC' : 'BC',
            tender_product_type: tenderProductType,
            tender_token: {
                token_value: '',
                protection_source: 'UC',
                processor_source: processor_type,
                token_type: 'CLAIM_CHECK_NONCE'
            },
            tender_alias: name,
            store: true,
            processor_type,
            processor_tender_id,
            processor_customer_id,
            tender_id: payment_method_id,
            requires_security_code_by,
            security_code,
            is_default,
            last_four,
            postal_code,
            expiry_month,
            expiry_year
        }
    }));
};

function _define_property$9(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$5(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$9(target, key, source[key]);
        });
    }
    return target;
}
const isGooglePayPaymentData = (data)=>typeof data === 'object' && !!data && 'payment_method_data' in data;
const shouldStepUpGooglePay = (data)=>{
    if (!data || !data.payment_method_data || !data.payment_method_data.info || !data.payment_method_data.info.assurance_details) {
        return true;
    }
    const { account_verified, card_holder_authenticated } = data.payment_method_data.info.assurance_details;
    return !account_verified || !card_holder_authenticated;
};
const mapToken = (paymentMethodData)=>{
    var _paymentMethodData_info, _paymentMethodData_info1, _paymentMethodData_info2;
    const tenderToken = JSON.parse(paymentMethodData.tokenization_data.token);
    // https://developers.google.com/pay/api/web/guides/resources/payment-data-cryptography#payment-method-token-structure
    // NB: Although the "outer" fields on the `paymentData` object will be snake_cased by the caller,
    // the `token` field would already be a JSON string, so will have the original pascalCase
    return _object_spread$5({
        version: tenderToken.protocolVersion,
        data: tenderToken.signedMessage,
        signature: tenderToken.signature
    }, ((_paymentMethodData_info = paymentMethodData.info) === null || _paymentMethodData_info === void 0 ? void 0 : _paymentMethodData_info.assurance_details) ? {
        assurance_details: {
            account_verified: (_paymentMethodData_info1 = paymentMethodData.info) === null || _paymentMethodData_info1 === void 0 ? void 0 : _paymentMethodData_info1.assurance_details.account_verified,
            card_holder_authenticated: (_paymentMethodData_info2 = paymentMethodData.info) === null || _paymentMethodData_info2 === void 0 ? void 0 : _paymentMethodData_info2.assurance_details.card_holder_authenticated
        }
    } : undefined, tenderToken.intermediateSigningKey ? {
        intermediate_signing_key: {
            signed_key: tenderToken.intermediateSigningKey.signedKey,
            signatures: tenderToken.intermediateSigningKey.signatures
        }
    } : undefined);
};
const tokenizeGooglePay = (sessionData, options)=>{
    var _options_payment_method_data_info_billing_address, _options_payment_method_data_info, _options_payment_method_data_info1;
    const tender = {
        customer_id: sessionData.customerId,
        customer_type: sessionData.customerType,
        session_id: sessionData.sessionId,
        email: sessionData.email || options.email || '',
        token: {
            tender_type: 'MP',
            tender_product_type: 'GP',
            tender_token: isGooglePayPaymentData(options) ? mapToken(options.payment_method_data) : {
                version: options.version,
                data: options.signed_message,
                signature: options.signature
            }
        },
        // Depending on the `billingAddressRequired` and `billingAddressParameters` used by the brand,
        // we either have:
        // - billing_address = undefined when billingAddressRequired is false
        // - billing_address = Name, country code, and postal code when billingAddressRequired is true
        //   and billingAddressParameters.format is "MIN"
        // - billing_address = full `GooglePayAddress` when billingAddressRequired is true and
        //   billingAddressParameters.format is "FULL"
        // https://developers.google.com/pay/api/web/reference/request-objects#BillingAddressParameters
        name: isGooglePayPaymentData(options) ? (_options_payment_method_data_info = options.payment_method_data.info) === null || _options_payment_method_data_info === void 0 ? void 0 : (_options_payment_method_data_info_billing_address = _options_payment_method_data_info.billing_address) === null || _options_payment_method_data_info_billing_address === void 0 ? void 0 : _options_payment_method_data_info_billing_address.name : undefined,
        address: isGooglePayPaymentData(options) && ((_options_payment_method_data_info1 = options.payment_method_data.info) === null || _options_payment_method_data_info1 === void 0 ? void 0 : _options_payment_method_data_info1.billing_address) ? {
            line1: options.payment_method_data.info.billing_address.address1 || '',
            line2: options.payment_method_data.info.billing_address.address2,
            locality: options.payment_method_data.info.billing_address.locality || '',
            administrative_area: options.payment_method_data.info.billing_address.administrative_area || '',
            // https://developers.google.com/pay/api/web/reference/response-objects
            // If we have a `billingAddress`, these must be defined, as they're part
            // of the "MIN" address format
            country: options.payment_method_data.info.billing_address.country_code,
            postal_code: options.payment_method_data.info.billing_address.postal_code
        } : undefined
    };
    const encodedTender = base64Encode(JSON.stringify(tender));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return encodedTender;
};

const tokenizeBraintree = (sessionData, options)=>{
    const tender = {
        customer_id: sessionData.customerId,
        customer_type: sessionData.customerType,
        session_id: sessionData.sessionId,
        email: sessionData.email || '',
        token: {
            tender_type: 'MP',
            tender_product_type: options.type === 'PayPalAccount' ? 'PA' : 'VE',
            tender_token: {
                nonce: options.nonce,
                deviceRawData: options.deviceRawData
            }
        }
    };
    if (options.type === 'PayPalAccount') {
        if (tender.email === '') {
            tender.email = options.details.email || '';
        }
        tender.token.tender_token.payerId = options.details.payerId;
        tender.name = options.details.firstName || options.details.lastName ? `${options.details.firstName || ''} ${options.details.lastName || ''}` : undefined;
        if (options.details.shippingAddress || options.details.billingAddress) {
            var _options_details_shippingAddress, _options_details_billingAddress, _options_details_shippingAddress1, _options_details_billingAddress1, _options_details_shippingAddress2, _options_details_billingAddress2, _options_details_shippingAddress3, _options_details_billingAddress3, _options_details_shippingAddress4, _options_details_billingAddress4;
            tender.address = {
                country: options.details.countryCode || '',
                postal_code: ((_options_details_shippingAddress = options.details.shippingAddress) === null || _options_details_shippingAddress === void 0 ? void 0 : _options_details_shippingAddress.postalCode) || ((_options_details_billingAddress = options.details.billingAddress) === null || _options_details_billingAddress === void 0 ? void 0 : _options_details_billingAddress.postalCode) || '',
                administrative_area: ((_options_details_shippingAddress1 = options.details.shippingAddress) === null || _options_details_shippingAddress1 === void 0 ? void 0 : _options_details_shippingAddress1.state) || ((_options_details_billingAddress1 = options.details.billingAddress) === null || _options_details_billingAddress1 === void 0 ? void 0 : _options_details_billingAddress1.state) || '',
                locality: ((_options_details_shippingAddress2 = options.details.shippingAddress) === null || _options_details_shippingAddress2 === void 0 ? void 0 : _options_details_shippingAddress2.city) || ((_options_details_billingAddress2 = options.details.billingAddress) === null || _options_details_billingAddress2 === void 0 ? void 0 : _options_details_billingAddress2.city) || '',
                line1: ((_options_details_shippingAddress3 = options.details.shippingAddress) === null || _options_details_shippingAddress3 === void 0 ? void 0 : _options_details_shippingAddress3.line1) || ((_options_details_billingAddress3 = options.details.billingAddress) === null || _options_details_billingAddress3 === void 0 ? void 0 : _options_details_billingAddress3.line1) || '',
                line2: ((_options_details_shippingAddress4 = options.details.shippingAddress) === null || _options_details_shippingAddress4 === void 0 ? void 0 : _options_details_shippingAddress4.line2) || ((_options_details_billingAddress4 = options.details.billingAddress) === null || _options_details_billingAddress4 === void 0 ? void 0 : _options_details_billingAddress4.line2)
            };
        }
    }
    if (options.type === 'VenmoAccount') {
        var _options_details_payerInfo, _options_details_payerInfo1;
        /**
     * Populate AVS data for Venmo if it present in response
     * https://developer.paypal.com/braintree/docs/guides/venmo/client-side/javascript/v3/#shipping-and-billing-address-collection
     */ if (((_options_details_payerInfo = options.details.payerInfo) === null || _options_details_payerInfo === void 0 ? void 0 : _options_details_payerInfo.billingAddress) || ((_options_details_payerInfo1 = options.details.payerInfo) === null || _options_details_payerInfo1 === void 0 ? void 0 : _options_details_payerInfo1.shippingAddress)) {
            var _options_details_payerInfo_shippingAddress, _options_details_payerInfo_billingAddress, _options_details_payerInfo_shippingAddress1, _options_details_payerInfo_billingAddress1, _options_details_payerInfo_shippingAddress2, _options_details_payerInfo_billingAddress2, _options_details_payerInfo_shippingAddress3, _options_details_payerInfo_billingAddress3, _options_details_payerInfo_shippingAddress4, _options_details_payerInfo_billingAddress4;
            tender.address = {
                country: '',
                postal_code: ((_options_details_payerInfo_shippingAddress = options.details.payerInfo.shippingAddress) === null || _options_details_payerInfo_shippingAddress === void 0 ? void 0 : _options_details_payerInfo_shippingAddress.postalCode) || ((_options_details_payerInfo_billingAddress = options.details.payerInfo.billingAddress) === null || _options_details_payerInfo_billingAddress === void 0 ? void 0 : _options_details_payerInfo_billingAddress.postalCode) || '',
                administrative_area: ((_options_details_payerInfo_shippingAddress1 = options.details.payerInfo.shippingAddress) === null || _options_details_payerInfo_shippingAddress1 === void 0 ? void 0 : _options_details_payerInfo_shippingAddress1.adminArea1) || ((_options_details_payerInfo_billingAddress1 = options.details.payerInfo.billingAddress) === null || _options_details_payerInfo_billingAddress1 === void 0 ? void 0 : _options_details_payerInfo_billingAddress1.adminArea1) || '',
                locality: ((_options_details_payerInfo_shippingAddress2 = options.details.payerInfo.shippingAddress) === null || _options_details_payerInfo_shippingAddress2 === void 0 ? void 0 : _options_details_payerInfo_shippingAddress2.adminArea2) || ((_options_details_payerInfo_billingAddress2 = options.details.payerInfo.billingAddress) === null || _options_details_payerInfo_billingAddress2 === void 0 ? void 0 : _options_details_payerInfo_billingAddress2.adminArea2) || '',
                line1: ((_options_details_payerInfo_shippingAddress3 = options.details.payerInfo.shippingAddress) === null || _options_details_payerInfo_shippingAddress3 === void 0 ? void 0 : _options_details_payerInfo_shippingAddress3.addressLine1) || ((_options_details_payerInfo_billingAddress3 = options.details.payerInfo.billingAddress) === null || _options_details_payerInfo_billingAddress3 === void 0 ? void 0 : _options_details_payerInfo_billingAddress3.addressLine1) || '',
                line2: ((_options_details_payerInfo_shippingAddress4 = options.details.payerInfo.shippingAddress) === null || _options_details_payerInfo_shippingAddress4 === void 0 ? void 0 : _options_details_payerInfo_shippingAddress4.addressLine2) || ((_options_details_payerInfo_billingAddress4 = options.details.payerInfo.billingAddress) === null || _options_details_payerInfo_billingAddress4 === void 0 ? void 0 : _options_details_payerInfo_billingAddress4.addressLine2) || ''
            };
        }
    }
    const encodedTender = base64Encode(JSON.stringify(tender));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return encodedTender;
};

function _define_property$8(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
const isError = (error)=>error instanceof Error;
const ensureError = (error)=>{
    if (isError(error)) {
        return error;
    }
    try {
        return new Error(JSON.stringify(error));
    } catch (serializationError) {
        // Unlikely, but for type safety
        return new Error('Unserializable error', {
            cause: serializationError
        });
    }
};
// Copy of
// https://github.com/bjyoungblood/es6-error/blob/de239cff000951417662bd4ce4a9c5a2721f1cd7/src/index.js
class BaseError extends Error {
    constructor(message = '', options){
        super(message, options);
        // extending Error is weird and does not propagate `message`
        Object.defineProperty(this, 'message', {
            configurable: true,
            enumerable: false,
            value: message,
            writable: true
        });
        Object.defineProperty(this, 'name', {
            configurable: true,
            enumerable: false,
            value: this.constructor.name,
            writable: true
        });
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
            return;
        }
        Object.defineProperty(this, 'stack', {
            configurable: true,
            enumerable: false,
            value: new Error(message).stack,
            writable: true
        });
    }
}
class SessionExpiredError extends BaseError {
    constructor(options){
        super('Session expired', options);
    }
}
class UnsupportedMethodError extends BaseError {
    constructor(context, options){
        super('Processor does not support method', options);
        _define_property$8(this, "context", void 0);
        this.context = context;
    }
}
class UninitializedSessionError extends BaseError {
    constructor(options){
        super('Cannot call method before session initialization', options);
    }
}
class ConfigError extends BaseError {
    constructor(context, options){
        super('Invalid config property', options);
        _define_property$8(this, "context", void 0);
        this.context = context;
    }
}
class ProcessorLoadError extends BaseError {
    constructor(context, options){
        super(`Processor ${context.failedDependency || 'SDK'} failed to load`, options);
        _define_property$8(this, "context", void 0);
        this.context = context;
    }
}
class ProcessorTimeoutError extends BaseError {
    constructor(context, options){
        super(`Processor ${context.failedDependency || 'SDK'} fetch timed out`, options);
        _define_property$8(this, "context", void 0);
        this.context = context;
    }
}
class HTMLElementNotFoundError extends BaseError {
    constructor(context, options){
        super('HTMLElement of type not found', options);
        _define_property$8(this, "context", void 0);
        this.context = context;
    }
}
class RetryableError extends BaseError {
    constructor(message = '', options){
        super(message, options);
    }
}
class DuplicatePendingAuthorizationHandleError extends BaseError {
    constructor(options){
        super('Pending authorization has already been handled', options);
    }
}

/* eslint-disable no-undef */ function asyncGeneratorStep$4(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$4(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$4(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$4(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$7(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$4(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$7(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$3(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$3(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$3(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
class FDuCom {
    _getTenderMapping() {
        return {
            CREDIT: 'BC',
            DEBIT: 'BC',
            PREPAID: 'GC'
        };
    }
    setConfig(key, value) {
        this._configStore[key] = value;
    }
    getConfig(key) {
        return this._configStore[key];
    }
    _getCardTypeMapping() {
        return {
            VISA: 'VS',
            MASTERCARD: 'MC',
            DISCOVER: 'DV',
            AMEX: 'AX',
            PREPAID: 'GC',
            VAULTED_ACCOUNT: 'VA',
            APPLE_PAY: 'AP',
            ANDRIOD_PAY: 'AD',
            PAYPAL: 'PA',
            TOKEN: 'TO',
            NONCE: 'NO',
            VCO: 'VC',
            VENMO: 'VE',
            AMAZON_PAY: 'AZ',
            VIPPS: 'VI',
            FLEET: 'FL'
        };
    }
    _getProtectionSource() {
        return {
            TRANS_ARMOR: 'TA',
            UCOM: 'UC',
            'No protection': 'NO'
        };
    }
    formTender(responseInput) {
        var _response;
        const response = JSON.parse(responseInput);
        if (!((_response = response) === null || _response === void 0 ? void 0 : _response.token)) {
            if (this.getConfig('debug')) {
                console.error(response);
            }
            throw new Error('Failed to tokenize submitted card');
        }
        const tender_token = {
            token_value: response.token.tokenId,
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            protection_source: this._getProtectionSource()[response.token.tokenProvider],
            processor_source: 'FD',
            token_type: response.token.tokenType
        };
        return {
            name: response.nickName,
            card_type: response.credit.cardType,
            last_four: response.credit.alias,
            payment_method: base64Encode(JSON.stringify({
                customer_id: this.getConfig('customerId'),
                customer_type: this.getConfig('customerType'),
                session_id: this.getConfig('sessionId'),
                email: this.getConfig('email'),
                token: {
                    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                    tender_type: this._getTenderMapping()[response.type],
                    tender_token,
                    processor_session_token: this.getConfig('accessToken'),
                    processor_customer_id: this.getConfig('processorCustomerId'),
                    tender_alias: response.nickName || `${response.credit.cardType.charAt(0).toUpperCase()}${response.credit.cardType.slice(1).toLowerCase()} ${response.credit.alias}`,
                    store: this.getConfig('customerType') !== 'registered' || this.getConfig('pageType') === 'GUEST_CHECKOUT' ? false : response.isSaveCard,
                    tender_product_type: // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                    this._getCardTypeMapping()[response.credit.cardType],
                    last_four: response.credit.alias
                }
            }))
        };
    }
    constructor(config){
        _define_property$7(this, "_configStore", {
            customerId: '',
            customerType: 'guest',
            sessionId: '',
            pageType: 'GUEST_CHECKOUT',
            accessToken: '',
            debug: false
        });
        this._configStore = {
            customerId: config.customerId,
            customerType: config.customerType,
            sessionId: config.sessionId,
            email: config.email,
            accessToken: config.bankCard.accessToken,
            processorCustomerId: config.bankCard.processorCustomerId,
            pageType: config.pageType,
            debug: !!config.debug
        };
    }
}
let fdUcom;
const onload$5 = ({ config, pageType, elementId, debug, onSuccess, onError, setFormValid, autoExit, onReady })=>{
    fdUcom = new FDuCom(_object_spread_props$3(_object_spread$4({}, config), {
        debug,
        pageType
    }));
    const end = (isError = false)=>{
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        if (isError || typeof ucomSDK.stop === 'function' && autoExit) {
            // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
            ucomSDK.stop();
        }
        if (typeof window.removeEventListener === 'function') {
            // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
            window.removeEventListener('message', ucomSDK.messageHandler);
        }
        /* eslint-disable @typescript-eslint/no-use-before-define */ if (typeof observer.disconnect === 'function') {
            observer.disconnect();
        }
    /* eslint-enable @typescript-eslint/no-use-before-define */ };
    const observer = new MutationObserver((mutations)=>{
        mutations.forEach((mutation)=>{
            mutation.removedNodes.forEach((node)=>{
                // @ts-expect-error TS(2339): Property 'id' does not exist on type 'Node'.
                if (node && (node.id === elementId || node.title === 'UCOM-SDK' || node.parentNode && node.parentNode.title === 'UCOM-SDK')) {
                    if (debug) {
                        console.log('Ucom SDK has been removed from the dom');
                    }
                    end();
                }
            });
        });
    });
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    observer.observe(document.getElementById(elementId).parentNode, {
        childList: true
    });
    try {
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        ucomSDK.init({
            accessToken: config.bankCard.accessToken,
            apiKey: config.bankCard.apiKey,
            fdCustomerId: config.bankCard.processorCustomerId,
            pageUrl: config.bankCard.hostedPages[pageType],
            redirectUrl: origin || 'null',
            mountId: elementId,
            encryptionKey: config.bankCard.encryptionKey,
            debug,
            store: pageType !== 'GUEST_CHECKOUT'
        }, (uComResponse)=>{
            try {
                const tender = fdUcom.formTender(uComResponse);
                onSuccess(tender);
                end();
            } catch (ex) {
                console.error(ex);
                onError(ex);
                end(true);
            }
        });
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        ucomSDK.start();
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        if (ucomSDK.on && typeof ucomSDK.on === 'function') {
            // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
            ucomSDK.on('change', (event)=>setFormValid(event.formValid || false));
            // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
            ucomSDK.on('ready', ()=>onReady());
            // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
            ucomSDK.on('error', (err)=>{
                onError(err);
                end(true);
            });
        }
    } catch (ex) {
        console.error(ex);
        onError(ex);
        end(true);
    }
};
const saveCard$3 = ()=>{
    // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
    if (typeof ucomSDK.triggerSaveAction === 'function') {
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        ucomSDK.triggerSaveAction();
    }
};
const stop$4 = ()=>{
    // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
    if (typeof ucomSDK.stop === 'function') {
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        ucomSDK.stop();
    }
};
const initDeviceDataCollection = function() {
    var _ref = _async_to_generator$4(function*(key = '') {
        var _RavelinPkg, _window;
        const RavelinPkg = yield import('ravelinjs/core.js');
        //When using modules, the default export of the ravelin package can be used. When using bundled js, ravelin is attached to the window
        const Ravelin = ((_RavelinPkg = RavelinPkg) === null || _RavelinPkg === void 0 ? void 0 : _RavelinPkg.default) || ((_window = window) === null || _window === void 0 ? void 0 : _window.Ravelin);
        const ravelin = new Ravelin({
            prefix: 'rjs-',
            key
        });
        const deviceId = yield ravelin.core.id();
        return deviceId;
    });
    return function initDeviceDataCollection() {
        return _ref.apply(this, arguments);
    };
}();
const shouldValidateForm$4 = true;
const refresh = (config)=>{
    const fiservExtendedConfig = config;
    // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
    if (typeof ucomSDK.updateSession === 'function') {
        fdUcom.setConfig('accessToken', fiservExtendedConfig.bankCard.accessToken);
        // @ts-expect-error TS(2304): Cannot find name 'ucomSDK'.
        ucomSDK.updateSession({
            accessToken: fiservExtendedConfig.bankCard.accessToken,
            encryptionKey: fiservExtendedConfig.bankCard.encryptionKey
        });
    } else {
        throw new Error('updateSession not implemented by ucomSDK');
    }
};

var fd = /*#__PURE__*/Object.freeze({
  __proto__: null,
  initDeviceDataCollection: initDeviceDataCollection,
  onload: onload$5,
  refresh: refresh,
  saveCard: saveCard$3,
  shouldValidateForm: shouldValidateForm$4,
  stop: stop$4
});

function _define_property$6(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
class FDUcomMock {
    getTender() {
        return this.tender;
    }
    fducommockSubmit() {
        this.resetButtons();
        this.setSubmitDisabled();
    }
    resetButtons() {
        const buttons = document.querySelectorAll('button');
        for(let i = 0; i < buttons.length; i++){
            buttons[i].style.color = 'black';
            // @ts-expect-error TS(7015): Element implicitly has an 'any' type because index... Remove this comment to see the full error message
            buttons[i].style['background-color'] = 'white';
        }
    }
    toggleSelectedButton(type) {
        this.resetButtons();
        const element = document.querySelector(`.${type}`);
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        element.style.color = 'white';
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        element.style['background-color'] = 'rgb(var(--pure-material-primary-rgb, 33, 150, 243))';
    }
    setSubmitEnabled() {
        const fducommockSubmit = document.querySelector('.fducommockSubmit');
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        fducommockSubmit.disabled = false;
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        fducommockSubmit.style.color = 'white';
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        fducommockSubmit.style['background-color'] = 'lightgreen';
    }
    setSubmitDisabled() {
        const fducommockSubmit = document.querySelector('.fducommockSubmit');
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        fducommockSubmit.disabled = true;
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        fducommockSubmit.style.color = 'darkgrey';
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        fducommockSubmit.style['background-color'] = 'white';
    }
    setToken(type) {
        this.toggleSelectedButton(type);
        this.setSubmitEnabled();
        if (type === 'bankcard') {
            this.tender = {
                name: 'Visa 1111',
                card_type: 'VISA',
                last_four: '1111',
                payment_method: base64Encode(JSON.stringify({
                    customer_id: this.config.customerId || v4(),
                    customer_type: this.config.customerType,
                    session_id: this.config.sessionId,
                    email: this.config.email,
                    token: {
                        tender_type: 'BC',
                        tender_token: {
                            token_value: 'b3959936-9288-4f61-a074-8bdd83d81fde',
                            protection_source: 'UC',
                            processor_source: 'FDM',
                            token_type: 'CLAIM_CHECK_NONCE'
                        },
                        processor_session_token: 'VaYIf1CpHzNZH7YrzLGZ0DA1OlQt',
                        processor_customer_id: this.config.bankCard.processorCustomerId,
                        tender_alias: 'Visa 1111',
                        store: !!this.config.bankCard.processorCustomerId,
                        tender_product_type: 'VS',
                        last_four: '1111'
                    }
                }))
            };
        } else if (type === 'googlepay') {
            this.tender = {
                name: 'Mobile Payment',
                card_type: 'Google Pay',
                last_four: 'N/A',
                payment_method: base64Encode(JSON.stringify({
                    customer_id: this.config.customerId || v4(),
                    customer_type: this.config.customerType,
                    session_id: this.config.sessionId,
                    email: this.config.email,
                    token: {
                        tender_type: 'MP',
                        tender_product_type: 'GP',
                        tender_token: {
                            version: 'ECv1',
                            data: '{"encryptedMessage":"2W1NU18IP95H+MxusJIBRP0K1/YFIZDJGOBbrLLhaE4yqEjUsIFtsqtbD3+T6JuYXlrnwKazT6Sn9xY9yS8+Z0a4GoEq7j4/o36/mjsu282xEtOHRYVhkCw2MlkkDrfMWtY95DERQSRDxRYvwWL+okuhgjwkWniTrmw6qtN+xFF3I/P8pNfFpzT3Xn1zssxfjIyv6dcrXiZnZynbvhQoc2k49DnmIg5kb78zKusu8ZUCmgLFGDu8UYypV7yS6zY3GuKJpqcyQ1aARETnnhm1QTrkjPQeto+5wBMJuDLm5bSbzRNFm4se6fol8yJESPJUoenZZmI+ph9LoRs3vfOY5bufEgXLJ7o2QRzPcqvjZKHVQorIbukRzzWImOsSviN6WVvh0tqQwECBbKGu8jtwLA3NlXlfjOd9ZXCiQhZmzz6HSJetK8/TBamugdhcif0kHXtDnJK8ADMS9jrTFhhdBxe++rLFqaDzW+WevQZoVHMSCRXfpjT3OLqR5Zh6WK20S1apy2TmV4Dag1Tq/sO4UkDR7o9ghqZZPcFUCXMEbdnx75sWNr/1Toqiiq40kBJJ35G9cRTYiFVpTzo8x9E\\u003d","ephemeralPublicKey":"BIdn6hw9ljHEU21uoTPJQJ4HQYwSxkZWmYsxwWDaVolqg/fRH7IKoTcQL80vg9ME5GzzAx+jchb4QOXrSU4xQC8\\u003d","tag":"m/1+qZX4vfz9/6E0FgdwE4GqmEHL/cZup1MFUvlrr0g\\u003d"}',
                            signature: 'MEUCIBfn/fwjccfSt5zeYh+55Rg6daE/Bl/CQb+Lrfxs+aiQAiEAxan1DFoQAu5SOPmt53WONmIzC75CaZvMT0OcUOOwEDg='
                        }
                    }
                }))
            };
        } else if (type === 'applepay') {
            this.tender = {
                name: 'Mobile Payment',
                card_type: 'Apple Pay',
                last_four: 'N/A',
                payment_method: base64Encode(JSON.stringify({
                    customer_id: this.config.customerId || v4(),
                    customer_type: this.config.customerType,
                    session_id: this.config.sessionId,
                    email: this.config.email,
                    token: {
                        tender_type: 'MP',
                        tender_product_type: 'AP',
                        tender_token: {
                            version: 'EC_v1',
                            data: 'iQhe7jDoda+55PsN6Ub0S9LDHYPkdPd/OlUskoe5tAYw5GkppykO6f2q3/ckpmuap5/WJDFW4b+RlzRODW1wX38iNHRXTbP3GJPW7xgRoUazUQ+DAg5f4WQm51GjNGre8G9yQhxSunPTLbSnxoUQPSCL58hMOVdyw/cDsdXvC9WmWCn/SNsIhNQVWKyPDYUfQv20FC3Fr7x3AQiDKgjkek8zMWBfCjkLh5+foDq2VxkDl7fCVnvnCmGwc5AVi3roLTemd+83jkrSeBMN233gNPBL6ze0Zp7xNEMFe1PwfEuaqEeVDKfLeJ+arFvvp0G43iXjVtvU7RH+6lX95gQ+lOaX4LtJukS8qYQ9axSj2oozEag5Uey+qfhyU55NVsXb+jErwslE83t9DkR56LapFEJ1RJFyCOjPpfjDy51cH0GLSLGaWMxLN0g7fHlTaNLo/A2H65Mptfzr6WaJr472lhMFWFzwsbMkQUdrGA==',
                            signature: 'MIAGCSqGSIb3DQEHAqCAMIACAQExDzANBglghkgBZQMEAgEFADCABgkqhkiG9w0BBwEAAKCAMIIB0zCCAXkCAQEwCQYHKoZIzj0EATB2MQswCQYDVQQGEwJVUzELMAkGA1UECAwCTkoxFDASBgNVBAcMC0plcnNleSBDaXR5MRMwEQYDVQQKDApGaXJzdCBEYXRhMRIwEAYDVQQLDAlGaXJzdCBBUEkxGzAZBgNVBAMMEmQxZHZ0bDEwMDAuMWRjLmNvbTAeFw0xOTA3MjUxNjI3NDlaFw0zOTA3MjAxNjI3NDlaMHYxCzAJBgNVBAYTAlVTMQswCQYDVQQIDAJOSjEUMBIGA1UEBwwLSmVyc2V5IENpdHkxEzARBgNVBAoMCkZpcnN0IERhdGExEjAQBgNVBAsMCUZpcnN0IEFQSTEbMBkGA1UEAwwSZDFkdnRsMTAwMC4xZGMuY29tMFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAErnHhPM18HFbOomJMUiLiPL7nrJuWvfPy0Gg3xsX3m8q0oWhTs1QcQDTT+TR3yh4sDRPqXnsTUwcvbrCOzdUEeTAJBgcqhkjOPQQBA0kAMEYCIQCXW2kjA36LdnEqc0qHb82FIYShdEk3hgPGxZDf6PfXygIhANrrpo/SY2YorozC73ZuQDtsGK3PHTdQZja5AgdEXy0yAAAxggFSMIIBTgIBATB7MHYxCzAJBgNVBAYTAlVTMQswCQYDVQQIDAJOSjEUMBIGA1UEBwwLSmVyc2V5IENpdHkxEzARBgNVBAoMCkZpcnN0IERhdGExEjAQBgNVBAsMCUZpcnN0IEFQSTEbMBkGA1UEAwwSZDFkdnRsMTAwMC4xZGMuY29tAgEBMA0GCWCGSAFlAwQCAQUAoGkwGAYJKoZIhvcNAQkDMQsGCSqGSIb3DQEHATAcBgkqhkiG9w0BCQUxDxcNMjAwODI0MDU1ODI0WjAvBgkqhkiG9w0BCQQxIgQgeeN49Wj2lQiLw5gzKWqhMpXcjGUa2ilnOzvNrq80LhYwCgYIKoZIzj0EAwIERjBEAiBEAD0GZKUlSZsFdfRwVFipyUtwlfpsCdQe+yDDhYqtuwIgJseSLTl9/l83qAmxXXEQft8fe1Gj/aQEMb7dJRBVQSoAAAAAAAA=',
                            decrypt_alias: 'merchant.com.applepay',
                            application_data: 'VEVTVA==',
                            header: {
                                ephemeral_public_key: 'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEcVpsjkzx3uU+mkA7V0gZeAZkeUNIBKrGwIxKMgoVCnvgSZDLDQYxZC7UFbeyg89eiEv1eZ2Ch38efO+xGvJhhg==',
                                public_key_hash: 'RLP3SbLJR7bWmJFAfBa8hV5BQhRJ1AAhBrsjkNXKW7U=',
                                transaction_id: '31323334353637'
                            }
                        }
                    }
                }))
            };
        }
    }
    constructor(config, debug){
        _define_property$6(this, "config", void 0);
        _define_property$6(this, "debug", void 0);
        _define_property$6(this, "tender", void 0);
        this.config = config;
        this.tender = {};
        this.debug = debug || false;
    }
}
const div = `
    <div
      style="
        width: 50%;
        height: fit-content;
        margin: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      "
    >
      <button
        class="bankcard"
        onClick="fducommockFunctions.setToken('bankcard')"
        style="
          margin: 10px;
          position: relative;
          display: inline-block;
          box-sizing: border-box;
          border: none;
          border-radius: 4px;
          padding: 0 16px;
          min-width: 125px;
          height: 36px;
          vertical-align: middle;
          text-align: center;
          text-overflow: ellipsis;
          text-transform: uppercase;
          color: black;
          background-color: white;
          box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
            0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
          font-family: var(
            --pure-material-font,
            'Roboto',
            'Segoe UI',
            BlinkMacSystemFont,
            system-ui,
            -apple-system
          );
          font-size: 14px;
          font-weight: 500;
          line-height: 36px;
          overflow: hidden;
          outline: none;
          cursor: pointer;
          transition: box-shadow 0.2s;
        "
      >
        Bank Card
      </button>
      <button
        class="googlepay"
        onClick="fducommockFunctions.setToken('googlepay')"
        style="
          margin: 10px;
          position: relative;
          display: inline-block;
          box-sizing: border-box;
          border: none;
          border-radius: 4px;
          padding: 0 16px;
          min-width: 125px;
          height: 36px;
          vertical-align: middle;
          text-align: center;
          text-overflow: ellipsis;
          text-transform: uppercase;
          color: black;
          background-color: white;
          box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
            0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
          font-family: var(
            --pure-material-font,
            'Roboto',
            'Segoe UI',
            BlinkMacSystemFont,
            system-ui,
            -apple-system
          );
          font-size: 14px;
          font-weight: 500;
          line-height: 36px;
          overflow: hidden;
          outline: none;
          cursor: pointer;
          transition: box-shadow 0.2s;
        "
      >
        Google Pay
      </button>
      <button
        class="applepay"
        onClick="fducommockFunctions.setToken('applepay')"
        style="
          margin: 10px;
          position: relative;
          display: inline-block;
          box-sizing: border-box;
          border: none;
          border-radius: 4px;
          padding: 0 16px;
          min-width: 125px;
          height: 36px;
          vertical-align: middle;
          text-align: center;
          text-overflow: ellipsis;
          text-transform: uppercase;
          color: black;
          background-color: white;
          box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
            0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
          font-family: var(
            --pure-material-font,
            'Roboto',
            'Segoe UI',
            BlinkMacSystemFont,
            system-ui,
            -apple-system
          );
          font-size: 14px;
          font-weight: 500;
          line-height: 36px;
          overflow: hidden;
          outline: none;
          cursor: pointer;
          transition: box-shadow 0.2s;
        "
      >
        Apple Pay
      </button>
      <button
        class="fducommockSubmit"
        type="submit"
        onClick="fducommockFunctions.fducommockSubmit()"
        disabled="true"
        style="
          margin: 10px;
          position: relative;
          display: inline-block;
          box-sizing: border-box;
          border: none;
          border-radius: 4px;
          padding: 0 16px;
          min-width: 125px;
          height: 36px;
          vertical-align: middle;
          text-align: center;
          text-overflow: ellipsis;
          text-transform: uppercase;
          color: darkgrey;
          background-color: white;
          box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
            0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
          font-family: var(
            --pure-material-font,
            'Roboto',
            'Segoe UI',
            BlinkMacSystemFont,
            system-ui,
            -apple-system
          );
          font-size: 14px;
          font-weight: 500;
          line-height: 36px;
          overflow: hidden;
          outline: none;
          cursor: pointer;
          transition: box-shadow 0.2s;
        "
      >
        submit
      </button>
    </div>
`;
const onload$4 = ({ config, elementId, debug, onSuccess, onError })=>{
    try {
        const element = document.getElementById(elementId);
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        element.innerHTML = div;
        const fducommockFunctions = new FDUcomMock(config, debug);
        // @ts-expect-error TS(7017): Element implicitly has an 'any' type because type ... Remove this comment to see the full error message
        window.fducommockFunctions = fducommockFunctions;
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        document.querySelector('.fducommockSubmit').onclick = ()=>{
            fducommockFunctions.fducommockSubmit();
            onSuccess(fducommockFunctions.getTender());
        };
    } catch (ex) {
        console.error(ex);
        onError(ex);
    }
};

var fdm = /*#__PURE__*/Object.freeze({
  __proto__: null,
  onload: onload$4
});

const DEFAULT_LOGO_URL = 'img/comercio.png';
/**
 * This script is stringified and inlined into the iframe
 * It is reponsible for clicking and hiding the niubiz button
 *
 * @returns {void}
 */ const iframeScript = ()=>{
    const sendError = (err)=>{
        window.parent.postMessage({
            error: err || new Error('unknown error')
        }, '*');
    };
    try {
        const originalOnError = window.onerror;
        window.onerror = (...args)=>{
            var _originalOnError;
            sendError(args[0]);
            (_originalOnError = originalOnError) === null || _originalOnError === void 0 ? void 0 : _originalOnError(...args);
        };
        /** @type HTMLElement | null */ const btn = document.querySelector('.start-js-btn');
        if (!btn) {
            sendError(new Error('No niubiz button found'));
            return;
        }
        // @ts-expect-error TS(2339): Property 'click' does not exist on type 'Element'.
        btn.click();
        // Hide the automatically clicked button
        // @ts-expect-error TS(2339): Property 'style' does not exist on type 'Element'.
        btn.style = 'visibility: hidden';
    } catch (e) {
        sendError(e);
    }
};
const buildMessageListener = ({ onSuccess, onError, config, iframe })=>(message)=>{
        try {
            var _message_data, _message_data_body, _message_data1;
            if (message.source && message.source !== iframe.contentWindow) {
                return;
            }
            // Added for testing
            if ((_message_data = message.data) === null || _message_data === void 0 ? void 0 : _message_data.testing) {
                return;
            }
            const token = (_message_data1 = message.data) === null || _message_data1 === void 0 ? void 0 : (_message_data_body = _message_data1.body) === null || _message_data_body === void 0 ? void 0 : _message_data_body.transactionToken;
            if (!token) {
                var _message_data_queryString, _message_data2, _message_data3;
                const timeoutMessage = ((_message_data2 = message.data) === null || _message_data2 === void 0 ? void 0 : (_message_data_queryString = _message_data2.queryString) === null || _message_data_queryString === void 0 ? void 0 : _message_data_queryString.timeout) ? 'timeout' : undefined;
                var _message_data_error, _ref;
                const errorMessage = (_ref = (_message_data_error = (_message_data3 = message.data) === null || _message_data3 === void 0 ? void 0 : _message_data3.error) !== null && _message_data_error !== void 0 ? _message_data_error : timeoutMessage) !== null && _ref !== void 0 ? _ref : 'No token';
                throw new Error(errorMessage);
            }
            onSuccess({
                payment_method: base64Encode(JSON.stringify({
                    customer_id: config.customerId,
                    customer_type: config.customerType,
                    session_id: config.sessionId,
                    email: config.email,
                    token: {
                        processor_type: 'NIUBIZ',
                        tender_type: 'BC',
                        tender_token: {
                            token_value: token
                        },
                        processor_session_token: config.bankCard.hostedPage.purchaseNumber
                    }
                }))
            });
        } catch (e) {
            onError(e);
        }
    };
/**
 * Takes the arguments and calls onSuccess with a "tender" object containing
 * the token from niubiz
 *
 * @param {{
 *  config: {
 *    customerId: string;
 *    customerType: string;
 *    sessionId: string;
 *    email: string;
 *    baseUrl: string;
 *    echoUrl: string;
 *    logoUrl?: string;
 *    script: string;
 *    bankCard: {
 *      hostedPage: {
 *        amount: number;
 *        keyExpirationTime: number;
 *        merchantId: string;
 *        purchaseNumber: string;
 *        sessionKey: string;
 *        echoToken: string;
 *      }
 *    }
 *  },
 *  pageType: string;
 *  elementId: string;
 *  debug: boolean;
 *  onSuccess: (tender: any) => void;
 *  onError: (error: any) => void;
 *  onReady: () => void;
 * }} options
 * @returns
 */ let stopFn;
const onload$3 = ({ config, elementId, onSuccess, onError, onReady })=>{
    try {
        var _iframe_contentWindow;
        const hostElement = document.getElementById(elementId);
        if (!hostElement) {
            onError(new Error('No element found'));
            return;
        }
        const iframeAlreadyAppended = hostElement.getElementsByTagName('iframe').length > 0;
        if (iframeAlreadyAppended) {
            return;
        }
        const { baseUrl, echoUrl, logoUrl = DEFAULT_LOGO_URL, scriptSource, bankCard: { hostedPage: { amount, keyExpirationTime, merchantId, purchaseNumber, sessionKey, echoToken } } } = config;
        hostElement.setAttribute('style', 'position: fixed; width: 100%; height: 100%; left: 0; top: 0; z-index: 2000');
        const iframe = document.createElement('iframe');
        iframe.setAttribute('id', 'yc-niubiz-wrapper-frame');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('scrolling', 'no');
        iframe.setAttribute('style', 'position: relative; width: 100%; height: 100%;');
        hostElement.appendChild(iframe);
        // eslint-disable-next-line prefer-const
        let listener;
        const wrapWithCleanup = (fn)=>(...args)=>{
                fn(...args);
                window.removeEventListener('message', listener);
                if (hostElement.contains(iframe)) {
                    hostElement.removeChild(iframe);
                }
                hostElement.setAttribute('style', '');
            };
        listener = buildMessageListener({
            config,
            onError: wrapWithCleanup(onError),
            onSuccess: wrapWithCleanup(onSuccess),
            iframe
        });
        window.addEventListener('message', listener);
        const checkModalLoaded = ()=>{
            var _iframe_contentDocument, _iframe;
            var _iframe_contentDocument_getElementsByTagName_length;
            const modalLoaded = ((_iframe_contentDocument_getElementsByTagName_length = (_iframe = iframe) === null || _iframe === void 0 ? void 0 : (_iframe_contentDocument = _iframe.contentDocument) === null || _iframe_contentDocument === void 0 ? void 0 : _iframe_contentDocument.getElementsByTagName('noscript').length) !== null && _iframe_contentDocument_getElementsByTagName_length !== void 0 ? _iframe_contentDocument_getElementsByTagName_length : 0) > 0 // a hacky way to detect of the niubiz iframe modal has loaded
            ;
            if (!modalLoaded) {
                setTimeout(checkModalLoaded, 100);
            } else {
                onReady();
                setTimeout(()=>{
                    // @ts-expect-error TS(2531): Object is possibly 'null'.
                    iframe.contentDocument.getElementById('close-button').style = 'visibility: visible';
                }, 400) // modal has 400ms opacity transition
                ;
            }
        };
        checkModalLoaded();
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        iframe.contentDocument.exitModal = wrapWithCleanup(()=>{});
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        stopFn = iframe.contentDocument.exitModal;
        const doc = (_iframe_contentWindow = iframe.contentWindow) === null || _iframe_contentWindow === void 0 ? void 0 : _iframe_contentWindow.document;
        if (!doc) {
            throw new Error('Unable to access iframe document');
        }
        const submitUrl = new URL(baseUrl);
        submitUrl.pathname = echoUrl;
        // Round down the expiration time to ensure that the UI times out before the session finishes
        // not after. Niubiz also has a min of 5 and max of 20
        const expirationTimeInMinutes = Math.min(Math.max(Math.floor((keyExpirationTime - Date.now()) / (60 * 1000)), 5), 20);
        doc.open();
        doc.write(`
    <head>
      <meta name="viewport" content="width=device-width,user-scalable=0">
    </head>
    <style>
      #close-button {
        position: absolute;
        left: calc(50% + 120px);
        top: calc(50% - 230px);
        z-index: 2147483647;
        border: none;
        background-color: transparent;
        color: rgb(51, 51, 51); /* match the modal background color so the button is hidden while the modal spinner is shown */
        visibility: hidden;
      }
    </style>
    <body>
      <button id="close-button" onClick="exitModal()">X</button>
      <form action="${submitUrl.toString()}?echoToken=${echoToken}" method="post">
        <script type="text/javascript" src="${scriptSource}"
          data-sessiontoken="${sessionKey}"
          data-channel="web"
          data-merchantid="${merchantId}"
          data-purchasenumber="${purchaseNumber}"
          data-amount="${amount.toFixed(2)}"
          data-expirationminutes="${expirationTimeInMinutes}"
          data-timeouturl="${submitUrl.toString()}?timeout=true&echoToken=${echoToken}"
          data-merchantlogo="${logoUrl}"
          data-formbuttoncolor="#000000"
          data-hidexbutton="true" // we can't use the Niubiz close modal button because there is no hook to allow us to remove the wrapping iframe
        ></script>
        <script>
          (${iframeScript.toString()})()
        </script>
      </form>
    </body>
    `);
        doc.close();
    } catch (e) {
        onError(ensureError(e));
    }
};
const stop$3 = ()=>{
    if (typeof stopFn === 'function') {
        stopFn();
    }
};
const shouldValidateForm$3 = false;

var niubiz = /*#__PURE__*/Object.freeze({
  __proto__: null,
  onload: onload$3,
  shouldValidateForm: shouldValidateForm$3,
  stop: stop$3
});

function validateConfig(config) {
    var _config, _config1;
    if (typeof config.scriptSource !== 'string') {
        throw new ConfigError({
            processor: 'ADYEN',
            property: 'scriptSource'
        });
    }
    if (typeof config.stylesSource !== 'string') {
        throw new ConfigError({
            processor: 'ADYEN',
            property: 'stylesSource'
        });
    }
    if (typeof ((_config = config) === null || _config === void 0 ? void 0 : _config.sdkIntegrity) !== 'string') {
        throw new ConfigError({
            processor: 'ADYEN',
            property: 'sdkIntegrity'
        });
    }
    if (typeof ((_config1 = config) === null || _config1 === void 0 ? void 0 : _config1.stylesIntegrity) !== 'string') {
        throw new ConfigError({
            processor: 'ADYEN',
            property: 'stylesIntegrity'
        });
    }
}

function asyncGeneratorStep$3(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$3(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$3(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$3(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$5(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$5(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const hasSDK = (window1)=>!!window1.AdyenCheckout && typeof window1.AdyenCheckout === 'function';
const loadSDK = function() {
    var _ref = _async_to_generator$3(function*({ scriptSource, scriptIntegrity, timeout }) {
        if (hasSDK(window)) {
            return Promise.resolve(window.AdyenCheckout);
        }
        const script = document.createElement('script');
        script.src = scriptSource;
        script.type = 'text/javascript';
        scriptIntegrity && (script.integrity = scriptIntegrity);
        script.crossOrigin = 'anonymous';
        script.async = true;
        return new Promise((resolve, reject)=>{
            const timeoutId = setTimeout(()=>reject(new ProcessorTimeoutError({
                    processor: 'ADYEN',
                    src: scriptSource,
                    timeout
                })), timeout);
            script.onload = ()=>{
                clearTimeout(timeoutId);
                if (hasSDK(window)) {
                    resolve(window.AdyenCheckout);
                } else {
                    reject(new ProcessorLoadError({
                        processor: 'ADYEN',
                        src: scriptSource
                    }));
                }
            };
            script.onerror = (event)=>{
                clearTimeout(timeoutId);
                reject(new ProcessorLoadError({
                    processor: 'ADYEN',
                    src: scriptSource
                }, {
                    cause: event instanceof Error ? event : undefined
                }));
            };
            document.head.appendChild(script);
        });
    });
    return function loadSDK(_) {
        return _ref.apply(this, arguments);
    };
}();
const loadStyles = ({ stylesSource, stylesIntegrity, timeout })=>{
    const link = document.createElement('link');
    link.href = stylesSource;
    link.rel = 'stylesheet';
    stylesIntegrity && (link.integrity = stylesIntegrity);
    link.crossOrigin = 'anonymous';
    return new Promise((resolve, reject)=>{
        const timeoutId = setTimeout(()=>reject(new ProcessorTimeoutError({
                processor: 'ADYEN',
                src: stylesSource,
                timeout,
                failedDependency: 'Styles'
            })), timeout);
        link.onload = ()=>{
            clearTimeout(timeoutId);
            // Assume it loads after timeout
            resolve();
        };
        link.onerror = (event)=>{
            clearTimeout(timeoutId);
            reject(new ProcessorLoadError({
                processor: 'ADYEN',
                src: stylesSource,
                failedDependency: 'Styles'
            }, {
                cause: event instanceof Error ? event : undefined
            }));
        };
        document.head.appendChild(link);
    });
};
const initialize = function() {
    var _ref = _async_to_generator$3(function*({ logger }, { config, options }) {
        validateConfig(config);
        const { scriptSource, sdkIntegrity, stylesSource, stylesIntegrity } = config;
        const LOADING_TIMEOUT_IN_MILLISECONDS = 10000;
        const [AdyenCheckout] = yield Promise.all([
            loadSDK({
                scriptSource,
                scriptIntegrity: sdkIntegrity,
                timeout: LOADING_TIMEOUT_IN_MILLISECONDS
            }),
            loadStyles({
                stylesSource,
                stylesIntegrity,
                timeout: LOADING_TIMEOUT_IN_MILLISECONDS
            })
        ]);
        // Ensure "core" options from the backend are not overridden
        const rest = _object_without_properties$1(options, [
            "clientKey",
            "environment",
            "showPayButton",
            "paymentMethodsResponse"
        ]);
        const checkout = yield AdyenCheckout(_object_spread$3({
            clientKey: config.bankCard.hostedPage.clientKey,
            environment: config.bankCard.hostedPage.environment,
            showPayButton: true,
            paymentMethodsResponse: {
                paymentMethods: config.bankCard.hostedPage.allowedPaymentMethods,
                storedPaymentMethods: config.bankCard.hostedPage.storedPaymentMethods
            }
        }, rest));
        return checkout;
    });
    return function initialize(_, _1) {
        return _ref.apply(this, arguments);
    };
}();

const sortObjectKeys = (obj)=>{
    if (Array.isArray(obj)) {
        return obj.map(sortObjectKeys);
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).sort().reduce((sortedObj, key)=>{
            sortedObj[key] = sortObjectKeys(obj[key]);
            return sortedObj;
        }, {});
    }
    return obj;
};

const createStandardTender = (options)=>{
    const config = options.config;
    const isRegisteredCustomer = config.customerType === 'registered';
    const shouldStorePaymentMethod = isRegisteredCustomer && !!options.storePaymentMethod;
    const paymentMethod = options.paymentMethod;
    const browserInfo = options.browserInfo;
    let token;
    switch(paymentMethod.type){
        case 'scheme':
            {
                token = {
                    processor_type: 'ADYEN',
                    tender_type: 'BC',
                    tender_alias: '',
                    tender_token: {
                        token_value: options.token,
                        encrypted_card_number: paymentMethod.encryptedCardNumber,
                        encrypted_expiry_month: paymentMethod.encryptedExpiryMonth,
                        encrypted_expiry_year: paymentMethod.encryptedExpiryYear,
                        encrypted_security_code: paymentMethod.encryptedSecurityCode
                    },
                    store: shouldStorePaymentMethod
                };
                break;
            }
        case 'googlepay':
            {
                if (paymentMethod.googlePayToken) {
                    const googleToken = JSON.parse(paymentMethod.googlePayToken);
                    token = {
                        processor_type: 'ADYEN',
                        tender_type: 'MP',
                        tender_alias: '',
                        tender_product_type: 'GP',
                        tender_token: {
                            version: googleToken.protocolVersion,
                            signature: googleToken.signature,
                            data: googleToken.signedMessage
                        },
                        store: shouldStorePaymentMethod
                    };
                } else {
                    throw new Error('Payment method GooglePay is missing required details');
                }
                break;
            }
        case 'applepay':
            {
                if (paymentMethod.applePayToken) {
                    const appleToken = JSON.parse(base64Decode(paymentMethod.applePayToken));
                    token = {
                        processor_type: 'ADYEN',
                        tender_type: 'MP',
                        tender_alias: '',
                        tender_product_type: 'AP',
                        tender_token: {
                            decrypt_alias: appleToken.decryptAlias,
                            application_data: appleToken.applicationData,
                            data: appleToken.data,
                            signature: appleToken.signature,
                            version: appleToken.version,
                            header: {
                                ephemeral_public_key: appleToken.header.ephemeralPublicKey,
                                public_key_hash: appleToken.header.publicKeyHash,
                                transaction_id: appleToken.header.transactionId
                            }
                        },
                        store: shouldStorePaymentMethod
                    };
                } else {
                    throw new Error('Payment method ApplePay is missing required details');
                }
                break;
            }
        case 'paypal':
            {
                if (paymentMethod.subtype) {
                    token = {
                        processor_type: 'ADYEN',
                        tender_type: 'MP',
                        tender_alias: '',
                        tender_product_type: 'PA',
                        tender_token: {
                            type: paymentMethod.type,
                            subtype: paymentMethod.subtype
                        },
                        store: shouldStorePaymentMethod
                    };
                } else {
                    throw new Error('Payment method Paypal is missing required details');
                }
                break;
            }
        default:
            {
                throw new Error('Unsupported payment type');
            }
    }
    return {
        customer_id: config.customerId,
        customer_type: config.customerType,
        session_id: config.sessionId,
        email: config.email,
        name: paymentMethod.holderName,
        returnUrl: options.returnUrl,
        browserInfo: browserInfo,
        token,
        origin: options.origin
    };
};
const createStoredPaymentTender = (options)=>{
    const config = options.config;
    const paymentMethod = options.paymentMethod;
    const browserInfo = options.browserInfo;
    return {
        customer_id: config.customerId,
        customer_type: config.customerType,
        session_id: config.sessionId,
        email: config.email,
        name: paymentMethod.holderName,
        token: {
            processor_type: 'ADYEN',
            tender_type: 'BC',
            tender_alias: '',
            tender_token: {
                token_value: options.token,
                stored_payment_method_id: paymentMethod.storedPaymentMethodId,
                encrypted_security_code: paymentMethod.encryptedSecurityCode
            },
            store: false
        },
        browserInfo: browserInfo,
        returnUrl: options.returnUrl
    };
};
const tokenize$2 = (options)=>{
    const tender = options.paymentMethod.storedPaymentMethodId ? createStoredPaymentTender(options) : createStandardTender(options);
    const encodedTender = base64Encode(JSON.stringify(sortObjectKeys(tender)));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return {
        payment_method: encodedTender
    };
};

function asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$2(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
const handlePendingAuthorization$1 = function() {
    var _ref = _async_to_generator$2(function*(options) {
        const { dropin, action } = options;
        yield dropin.handleAction(action);
    });
    return function handlePendingAuthorization(options) {
        return _ref.apply(this, arguments);
    };
}();

const stop$2 = ()=>{
// Empty function to prevent errors
};
const saveCard$2 = ()=>{
// Empty function to prevent errors
};
const onload$2 = ()=>{
// Empty function to prevent errors
};
const shouldValidateForm$2 = false;

var adyen = /*#__PURE__*/Object.freeze({
  __proto__: null,
  handlePendingAuthorization: handlePendingAuthorization$1,
  initialize: initialize,
  onload: onload$2,
  saveCard: saveCard$2,
  shouldValidateForm: shouldValidateForm$2,
  stop: stop$2,
  tokenize: tokenize$2
});

const tokenize$1 = (options)=>{
    const { config, paymentProcessorOverride } = options;
    const tender = {
        customer_id: config.customerId,
        customer_type: config.customerType,
        session_id: config.sessionId,
        email: config.email,
        token: {
            processor_type: paymentProcessorOverride || 'KNET',
            tender_type: 'BC',
            tender_token: {},
            store: false
        }
    };
    if (paymentProcessorOverride) {
        tender.token.processor_override = true;
    }
    const encodedTender = base64Encode(JSON.stringify(tender));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return {
        payment_method: encodedTender
    };
};

const stop$1 = ()=>{
// Empty function to prevent errors
};
const saveCard$1 = ()=>{
// Empty function to prevent errors
};
const onload$1 = ()=>{
// Empty function to prevent errors
};
const shouldValidateForm$1 = false;

var knet = /*#__PURE__*/Object.freeze({
  __proto__: null,
  onload: onload$1,
  saveCard: saveCard$1,
  shouldValidateForm: shouldValidateForm$1,
  stop: stop$1,
  tokenize: tokenize$1
});

const tokenize = (options)=>{
    const { config, paymentProcessorOverride } = options;
    const tender = {
        customer_id: config.customerId,
        customer_type: config.customerType,
        session_id: config.sessionId,
        email: config.email,
        token: {
            processor_type: paymentProcessorOverride || 'EVERTEC',
            tender_type: 'BC',
            tender_token: {},
            store: false
        }
    };
    if (paymentProcessorOverride) {
        tender.token.processor_override = true;
    }
    const encodedTender = base64Encode(JSON.stringify(tender));
    if (!encodedTender) {
        throw new Error('Failed to encode tender');
    }
    return {
        payment_method: encodedTender
    };
};

const stop = ()=>{
// Empty function to prevent errors
};
const saveCard = ()=>{
// Empty function to prevent errors
};
const onload = ()=>{
// Empty function to prevent errors
};
const shouldValidateForm = false;

var evertec = /*#__PURE__*/Object.freeze({
  __proto__: null,
  onload: onload,
  saveCard: saveCard,
  shouldValidateForm: shouldValidateForm,
  stop: stop,
  tokenize: tokenize
});

const processors = {
    fd,
    fdm,
    niubiz,
    adyen,
    knet,
    evertec
};
const isProcessorType = (key)=>key in processors;
const getProcessor = (processor_id)=>{
    if (!processor_id) {
        throw new Error('Invalid processor information received');
    }
    processor_id = processor_id.toLowerCase();
    const isMockProcessor = processor_id.endsWith('m');
    if (isMockProcessor) {
        processor_id = processor_id.slice(0, -1);
    }
    if (!isProcessorType(processor_id)) {
        throw new Error('Invalid processor information received');
    }
    return processors[processor_id];
};

var name = "@yc-commerce/payments-frontend-sdk";
var version = "5.15.1";
var description = "SDK to render card capture iframes";
var main = "dist/bundle.cjs";
var module = "dist/bundle.mjs";
var exports = {
	".": {
		"import": "./dist/bundle.mjs",
		require: "./dist/bundle.cjs",
		types: "./dist/index.d.ts"
	}
};
var files = [
	"/dist",
	"decs.d.ts"
];
var bundledDependencies = [
	"uuid",
	"ravelinjs"
];
var types = "dist/index.d.ts";
var sideEffects = false;
var author = "Sam Kumar";
var homepage = "https://gitlab.com/yumbrands/yumdev/yc-commerce/payments-frontend-sdk";
var repository = {
	type: "git",
	url: "https://gitlab.com/yumbrands/yumdev/yc-commerce/payments-frontend-sdk.git"
};
var publishConfig = {
	"@yc-commerce:registry": "https://gitlab.com/api/v4/projects/54346663/packages/npm/"
};
var scripts = {
	dev: "NODE_ENV='local' nodemon -e js,ts,html -w web -w examples  --exec \"npm run build && npm run example\"",
	"build:types": "tsc --emitDeclarationOnly -p tsconfig.lib.json",
	build: "rimraf dist/examples && npm run build:types && rollup -c --configIncludeExamples && rimraf dist/tmp",
	example: "NODE_ENV='local' node --loader @swc-node/register/esm ./examples/server.ts",
	test: "jest",
	lint: "./node_modules/.bin/eslint . --fix",
	"release:major": "npm version major && git push && git push --tags",
	"release:minor": "npm version minor && git push && git push --tags",
	"release:patch": "npm version patch && git push && git push --tags",
	"release:pre": "npm version prerelease && git push && git push --tags",
	getAuthTokenWithCartDev: "node --loader @swc-node/register/esm ./scripts/getAuthTokenWithCartDev.ts",
	script: "node --loader @swc-node/register/esm",
	typecheck: "npx tsc -p tsconfig.lib.json -p tsconfig.spec.json --noEmit"
};
var dependencies = {
	"@adyen/adyen-web": "^5.71.0",
	ravelinjs: "^1.8.1",
	uuid: "^8.3.2"
};
var devDependencies = {
	"@rollup/plugin-json": "^6.0.1",
	"@rollup/plugin-node-resolve": "^15.1.0",
	"@swc-node/register": "^1.6.6",
	"@swc/core": "^1.3.69",
	"@swc/jest": "^0.2.26",
	"@types/express": "^4.17.17",
	"@types/jest": "^29.2.1",
	"@types/uuid": "^9.0.2",
	"@typescript-eslint/eslint-plugin": "^6.1.0",
	"@typescript-eslint/parser": "^6.1.0",
	"@urql/core": "^3.0.5",
	eslint: "^7.24.0",
	"eslint-plugin-eslint-snake-case": "0.0.6",
	"eslint-plugin-import": "^2.7.0",
	"eslint-plugin-node": "^5.1.1",
	"eslint-plugin-promise": "^3.5.0",
	"eslint-plugin-standard": "^3.0.1",
	express: "^4.17.1",
	"express-http-proxy": "^1.6.2",
	jest: "^29.2.2",
	"jest-environment-jsdom": "^29.2.2",
	"node-fetch": "^3.2.10",
	nodemon: "^2.0.7",
	rimraf: "^6.0.1",
	rollup: "^3.26.3",
	"rollup-plugin-copy": "^3.5.0",
	"rollup-plugin-dts": "^6.2.0",
	"rollup-plugin-swc3": "^0.9.1",
	typescript: "^5.1.6"
};
var packageJson = {
	name: name,
	version: version,
	description: description,
	main: main,
	module: module,
	exports: exports,
	files: files,
	bundledDependencies: bundledDependencies,
	types: types,
	sideEffects: sideEffects,
	author: author,
	homepage: homepage,
	repository: repository,
	publishConfig: publishConfig,
	scripts: scripts,
	dependencies: dependencies,
	devDependencies: devDependencies
};

const createPaymentTokenMutation = `mutation createPaymentToken($input: CreatePaymentTokenInput!) {
    createPaymentToken(input: $input) {
      ... on CreatePaymentTokenSuccess {
        paymentToken
      }
      ... on PaymentProcessorError {
        message
        reason
      }
    }
  }`;

const beginPaymentSession = `mutation beginPaymentSession ($input: BeginPaymentSessionInput!) {
    beginPaymentSession (input: $input) {
        __typename
        ... on BeginPaymentSessionSuccess {
            sessionToken
            sessionConfig
            session {
                sessionId
                customer {
                    customerId
                }
                device {
                    deviceId
                }
            }
        }
        ... on PaymentSessionConfigurationError {
            message
            reason
        }
        ...on CybersourceCaptureContextTokenInvalidError {
            message
        }
        ...on CybersourceCaptureContextTokenExpiredError {
          message
        }
    }
}`;

const savedPaymentMethodsQuery = (includePostalCode, includeExpirationDate, includePrepaidBalance)=>`query savedPaymentMethods {
        savedPaymentMethods {
            edges {
                cursor
                node {
                    ... on SavedBankCard {
                        paymentMethodId
                        default
                        name
                        cardType
                        lastFour
                        requiresSecurityCodeBy
                        ${includePostalCode ? 'postalCode' : ''}
                        ${includeExpirationDate ? 'expiryMonth' : ''}
                        ${includeExpirationDate ? 'expiryYear' : ''}
                    }
                    ... on SavedGiftCard {
                        paymentMethodId
                        default
                        name
                        requiresSecurityCode
                        lastFour
                        ${includePrepaidBalance ? 'balance { currentBalance { amount } }' : ''}
                    }
                }
            }
            pageInfo {
                hasNextPage
                hasPreviousPage
            }
        }
    }`;

const updateSavedPaymentMethodMutation = `mutation updateSavedPaymentMethod($input: UpdateSavedPaymentMethodInput!) {
        updateSavedPaymentMethod(input: $input) {
          ... on UpdateSavedPaymentMethodSuccess {
            paymentMethod {
              ... on SavedBankCard {
                paymentMethodId
                default
                name
                cardType
                lastFour
                requiresSecurityCodeBy
              }
              ... on SavedGiftCard {
                paymentMethodId
                default
                name
                requiresSecurityCode
                lastFour
              }
            }
          }
          ... on UpdateSavedPaymentMethodError {
            message
          }
        }
      }`;

const saveDefaultPaymentMethodMutation = `mutation setDefaultSavedPaymentMethod($input: SetDefaultSavedPaymentMethodInput!) {
        setDefaultSavedPaymentMethod(input: $input) {
          ... on SetDefaultSavedPaymentMethodSuccess {
            paymentMethod {
              ... on SavedBankCard {
                paymentMethodId
                default
                name
                cardType
                lastFour
                requiresSecurityCodeBy
              }
              ... on SavedGiftCard {
                paymentMethodId
                default
                name
                requiresSecurityCode
                lastFour
              }
            }
          }
          ... on SetDefaultSavedPaymentMethodError {
            message
          }
        }
      }`;

const deleteSavedPaymentMethodMutation = `mutation deleteSavedPaymentMethod($input: DeleteSavedPaymentMethodInput!) {
        deleteSavedPaymentMethod(input: $input) {
          ... on DeleteSavedPaymentMethodSuccess {
            paymentMethod {
              ... on SavedBankCard {
                paymentMethodId
                default
                name
                cardType
                lastFour
                requiresSecurityCodeBy
              }
              ... on SavedGiftCard {
                paymentMethodId
                default
                name
                requiresSecurityCode
                lastFour
              }
            }
          }
          ... on DeleteSavedPaymentMethodError {
            message
          }
        }
      }`;

const setupPaymentFraudProtection = `mutation setupPaymentFraudProtection ($input: SetupPaymentFraudProtectionInput!) {
    setupPaymentFraudProtection(input: $input) {
        __typename
        ... on SetupPaymentFraudProtectionCybersourceSuccess {
            accessToken
            deviceDataCollectionUrl
        }
        ... on CybersourceTransientTokenExpiredError {
            message
        }
        ... on CybersourceTransientTokenInvalidError {
            message
        }
        ... on DuplicatePaymentOperationError {
            message
        }
        ... on PaymentSessionNotFoundError {
            message
        }
        ... on PaymentProcessorError {
            message
            reason
        }
        ... on UnsupportedPaymentProcessorOperationError {
            message
        }
        ... on SetupPaymentFraudProtectionCybersourceInputTokenConflictError {
            message
        }
        ... on PaymentMethodMismatchError {
            message
        }
    }
}`;

const updatePaymentSession = `mutation updatePaymentSession ($input: UpdatePaymentSessionInput!) {
    updatePaymentSession (input: $input) {
        __typename
        ... on UpdatePaymentSessionSuccess {
            session {
                sessionId
                customer {
                    isGuest
                }
                device {
                    deviceId
                }
            }
        }
        ... on PaymentSessionNotFoundError {
            message
        }
    }
}`;

const createSavedPaymentMethodMutation = `mutation createSavedPaymentMethod ($input: CreateSavedPaymentMethodInput!) {
    createSavedPaymentMethod (input: $input) {
        ... on AddSavedPaymentMethodSuccess {
            paymentMethod {
                ... on SavedBankCard {
                    paymentMethodId
                    default
                    name
                    cardType
                    lastFour
                    postalCode
                    expiryMonth
                    expiryYear
                    requiresSecurityCodeBy
                    imageUrl
                }
                ... on SavedGiftCard {
                    paymentMethodId
                    default
                    name
                    requiresSecurityCode
                    lastFour
                    balance { currentBalance { amount } }
                    imageUrl
                }
            }
        }
        ... on AddSavedPaymentMethodError {
            message
            reasonCode
        }
    }
}`;

const refreshPaymentSessionMutation = `mutation refreshPaymentSession ($input: RefreshPaymentSessionInput!) {
    refreshPaymentSession (input: $input) {
        __typename
        ... on UpdatePaymentSessionSuccess {
            sessionConfig
            session {
                sessionId
                customer {
                    isGuest
                }
                device {
                    deviceId
                }
            }
        }
        ... on PaymentSessionNotFoundError {
            message
        }
        ... on PaymentProcessorError {
            message
        }
        ... on UnsupportedPaymentProcessorOperationError {
            message
        }
    }
}`;

const forcePendingAuthorizationResolution = `mutation forcePendingAuthorizationResolution ($input: ForcePendingAuthorizationResolutionInput!) {
    forcePendingAuthorizationResolution(input: $input) {
        __typename
        ... on ForcePendingAuthorizationResolutionSuccess {
            sessionId
            orderId
        }
        ... on PendingAuthorizationNotFoundError {
            message
        }
        ... on PaymentSessionNotFoundError {
            message
        }
        ... on UnsupportedPaymentProcessorOperationError {
            message
        }
        ... on DuplicatePaymentOperationError {
            message
        }
    }
}`;

function _define_property$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
class Logger {
    debug(...args) {
        if (this.isDebug) {
            console.debug(...args);
        }
    }
    log(...args) {
        console.log(...args);
    }
    info(...args) {
        console.info(...args);
    }
    warn(...args) {
        console.warn(...args);
    }
    error(...args) {
        console.error(...args);
    }
    constructor(isDebug){
        _define_property$4(this, "isDebug", void 0);
        this.isDebug = isDebug;
    }
}

const isElementType = (element, tagName)=>{
    return element.nodeName.toLowerCase() === tagName;
};
/**
 * Resolve `html_element` and `html_element_id` YCCommercePayments[Admin] class method options
 * to a HTMLDivElement.
 *
 * Throws a HTMLElementNotFoundError if no `div` element is found based on the options
 */ const resolveHTMLElementOptions = (/**
   * `options` object from the public API of a YCCommercePayments[Admin] class method
   */ options, { defaultHTMLElementId })=>{
    const { html_element, html_element_id } = options;
    if (html_element) {
        if (!isElementType(html_element, 'div')) {
            throw new HTMLElementNotFoundError({
                id: html_element.id,
                type: 'div'
            });
        }
        return html_element;
    }
    const id = html_element_id !== null && html_element_id !== void 0 ? html_element_id : defaultHTMLElementId;
    const element = document.querySelector(`div#${id}`);
    if (!element) {
        throw new HTMLElementNotFoundError({
            id,
            type: 'div'
        });
    }
    return element;
};

function _define_property$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$3(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$2(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const handlePendingAuthorization = (dependencies, options)=>{
    const { processorType } = dependencies.config;
    const supportedProcessorIds = [
        'cybersource',
        'adyen'
    ];
    if (!supportedProcessorIds.includes(processorType.toLowerCase())) {
        throw new UnsupportedMethodError({
            processor: processorType
        });
    }
    if (processorType.toLowerCase().startsWith('cybersource')) {
        const otherOptions = _object_without_properties(options, [
            "html_element",
            "html_element_id"
        ]);
        const container = resolveHTMLElementOptions(options, {
            defaultHTMLElementId: 'yc-payments-pendingauthorization'
        });
        return import('./cybersource.mjs').then(({ handlePendingAuthorization })=>{
            handlePendingAuthorization(_object_spread_props$2(_object_spread$2({}, otherOptions), {
                container,
                logger: dependencies.logger
            })).then(()=>{
                var _options_onReady, _options;
                return (_options_onReady = (_options = options).onReady) === null || _options_onReady === void 0 ? void 0 : _options_onReady.call(_options);
            }).catch((error)=>{
                var _options_onError, _options;
                dependencies.logger.error(error, 'failed to process to pending authorization, closing');
                (_options_onError = (_options = options).onError) === null || _options_onError === void 0 ? void 0 : _options_onError.call(_options, error);
            });
        }).catch((error)=>{
            dependencies.logger.error(error, 'failed to import process to handle pending authorization, closing');
        });
    }
    if (processorType.toLowerCase().startsWith('adyen')) {
        // Note: Adyen performs full-page redirects so we are not able to use the onComplete handlers while processing
        // a pending authorization.
        const { action, dropin, sessionId } = options;
        return Promise.resolve().then(function () { return adyen; }).then(({ handlePendingAuthorization })=>{
            handlePendingAuthorization({
                action,
                dropin,
                sessionId,
                logger: dependencies.logger
            }).catch((error)=>{
                var _options_onError, _options;
                dependencies.logger.error(error, 'failed to process to pending authorization, closing');
                (_options_onError = (_options = options).onError) === null || _options_onError === void 0 ? void 0 : _options_onError.call(_options, error);
            });
        }).catch((error)=>{
            dependencies.logger.error(error, 'failed to import process to handle pending authorization, closing');
        });
    }
    throw new UnsupportedMethodError({
        processor: processorType
    });
};
const hasSeenPendingAuthorizationChallenge = (dependencies, options)=>{
    const { processorType } = dependencies.config;
    if (processorType.toLowerCase() !== 'cybersource') {
        throw new UnsupportedMethodError({
            processor: processorType
        });
    }
    return import('./cybersource.mjs').then(({ hasSeenPendingAuthorizationChallenge })=>{
        var _options_onComplete, _options;
        const hasSeenChallenge = hasSeenPendingAuthorizationChallenge(_object_spread_props$2(_object_spread$2({}, options), {
            logger: dependencies.logger
        }));
        (_options_onComplete = (_options = options).onComplete) === null || _options_onComplete === void 0 ? void 0 : _options_onComplete.call(_options, {
            hasSeenChallenge
        });
    });
};
const markPendingAuthorizationChallengeAsSeen = (dependencies, options)=>{
    const { processorType } = dependencies.config;
    if (processorType.toLowerCase() !== 'cybersource') {
        throw new UnsupportedMethodError({
            processor: processorType
        });
    }
    return import('./cybersource.mjs').then(({ markPendingAuthorizationChallengeAsSeen })=>{
        var _options_onComplete, _options;
        markPendingAuthorizationChallengeAsSeen(_object_spread_props$2(_object_spread$2({}, options), {
            logger: dependencies.logger
        }));
        (_options_onComplete = (_options = options).onComplete) === null || _options_onComplete === void 0 ? void 0 : _options_onComplete.call(_options);
    });
};
const discardPendingAuthorizationChallengeSeenMark = (dependencies, options)=>{
    const { processorType } = dependencies.config;
    if (processorType.toLowerCase() !== 'cybersource') {
        throw new UnsupportedMethodError({
            processor: processorType
        });
    }
    return import('./cybersource.mjs').then(({ discardPendingAuthorizationChallengeSeenMark })=>{
        var _options_onComplete, _options;
        discardPendingAuthorizationChallengeSeenMark(_object_spread_props$2(_object_spread$2({}, options), {
            logger: dependencies.logger
        }));
        (_options_onComplete = (_options = options).onComplete) === null || _options_onComplete === void 0 ? void 0 : _options_onComplete.call(_options);
    });
};

// https://github.com/graphql/graphql-spec/blame/October2021/spec/Section%207%20--%20Response.md#L37
const isSuccessfulGraphQLResponse = (response)=>typeof response === 'object' && !!response && 'data' in response && typeof response['data'] === 'object' && !!response['data'];

const isSuccessfulSetupPaymentFraudProtectionResponse = (response)=>{
    var _setupPaymentFraudProtectionResponse, _setupPaymentFraudProtectionResponse1, _setupPaymentFraudProtectionResponse2;
    if (!(isSuccessfulGraphQLResponse(response) && 'setupPaymentFraudProtection' in response.data && typeof response.data.setupPaymentFraudProtection === 'object' && !!response.data.setupPaymentFraudProtection)) {
        return false;
    }
    const setupPaymentFraudProtectionResponse = response.data.setupPaymentFraudProtection;
    return ((_setupPaymentFraudProtectionResponse = setupPaymentFraudProtectionResponse) === null || _setupPaymentFraudProtectionResponse === void 0 ? void 0 : _setupPaymentFraudProtectionResponse.__typename) === 'SetupPaymentFraudProtectionCybersourceSuccess' && !!((_setupPaymentFraudProtectionResponse1 = setupPaymentFraudProtectionResponse) === null || _setupPaymentFraudProtectionResponse1 === void 0 ? void 0 : _setupPaymentFraudProtectionResponse1.accessToken) && !!((_setupPaymentFraudProtectionResponse2 = setupPaymentFraudProtectionResponse) === null || _setupPaymentFraudProtectionResponse2 === void 0 ? void 0 : _setupPaymentFraudProtectionResponse2.deviceDataCollectionUrl);
};

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$2(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
class YCCommercePaymentsAdmin {
    getExtendedConfig() {
        return _object_spread_props$1(_object_spread$1({}, this.config), {
            sessionId: this.session_id,
            customerType: this.customer_type
        });
    }
    executeCaptureCard(options) {
        var _options_html_element, _options, _options1;
        const elementId = ((_options = options) === null || _options === void 0 ? void 0 : (_options_html_element = _options.html_element) === null || _options_html_element === void 0 ? void 0 : _options_html_element.id) || ((_options1 = options) === null || _options1 === void 0 ? void 0 : _options1.html_element_id) || 'yc-payments-cardcapture';
        const pageType = options.page_type || 'GUEST_CHECKOUT';
        const onError = options.onError || (()=>null);
        const onSuccess = options.onSuccess || (()=>null);
        const onReady = options.onReady || (()=>null);
        const autoExit = options.auto_exit !== false;
        try {
            if (!this.session_id) {
                return onError(new UninitializedSessionError());
            }
            if (!elementId) {
                return onError(new Error(`Invalid DOM element received, no element id: ${elementId}`));
            }
            YCCommercePaymentsAdmin.checkIfDivExists(elementId);
            if (this.customer_type === 'guest' && pageType !== 'GUEST_CHECKOUT') {
                return onError(new Error('Cannot load registered user forms for guest user'));
            }
            const config = this.getExtendedConfig();
            this.mountDiv({
                elementId,
                pageType,
                onError,
                onSuccess,
                onReady,
                config,
                type: 'BANK_CARD',
                autoExit
            });
        } catch (ex) {
            this.logger.error(ex);
            onError(ex);
        }
    }
    captureCard(options) {
        return this.executeCaptureCard(options);
    }
    mountDiv({ elementId, pageType, onError, onSuccess, onReady, config, type, autoExit }) {
        try {
            const debug = this.debug;
            const div = document.getElementById(elementId);
            let loadProcessor;
            switch(type){
                /* support for future cases */ case 'BANK_CARD':
                    loadProcessor = ()=>{
                        const processor = getProcessor(config.processorType);
                        if (typeof processor.onload !== 'function') {
                            throw new Error(`processor function is misconfigured for id '${config.processorType}'`);
                        }
                        if (processor.shouldValidateForm) {
                            this.form_is_valid = false;
                        }
                        processor.onload({
                            config,
                            pageType,
                            elementId,
                            debug,
                            onSuccess,
                            onError,
                            onReady,
                            setFormValid: (isValidState)=>{
                                this.form_is_valid = isValidState;
                            },
                            autoExit
                        });
                    };
                    if (config.scriptSource) {
                        const script = document.createElement('script');
                        script.src = config.scriptSource;
                        if (config.sdkIntegrity) {
                            script.crossOrigin = 'anonymous';
                            script.integrity = config.sdkIntegrity;
                        }
                        script.onload = loadProcessor;
                        // @ts-expect-error TS(2531): Object is possibly 'null'.
                        div.appendChild(script);
                        if (config.stylesSource) {
                            const stylesheet = document.createElement('link');
                            stylesheet.rel = 'stylesheet';
                            stylesheet.href = config.stylesSource;
                            if (config.stylesIntegrity) {
                                stylesheet.integrity = config.stylesIntegrity;
                            }
                            stylesheet.crossOrigin = 'anonymous';
                            if (div) {
                                div.appendChild(stylesheet);
                            }
                        }
                    } else {
                        loadProcessor();
                    }
                    break;
                default:
                    return onError(new Error(`cannot mount form for type '${type}'`));
            }
        } catch (ex) {
            this.logger.error(ex);
            onError(ex);
        }
    }
    logDebug(...args) {
        if (this.debug) {
            this.logger.debug(...args);
        }
    }
    static checkIfElementExists(element_id, type) {
        const element = document.getElementById(element_id);
        if (!element) {
            throw new Error(`No element found with id "${element_id}"`);
        }
        if (element.nodeName && element.nodeName.toLowerCase() !== type) {
            throw new Error(`No "${type}" element found with id "${element_id}"`);
        }
        return true;
    }
    static checkIfDivExists(element_id) {
        return this.checkIfElementExists(element_id, 'div');
    }
    isConsumerAuthenticationEnabled() {
        if (!this.config) {
            throw new Error('Payment session configuration not found');
        }
        return !this.config.disableConsumerAuthentication;
    }
    tokenizeApplePay(options, settings) {
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        return tokenizeApplePay({
            customerId: this.customer_id,
            customerType: this.customer_type,
            sessionId: this.session_id,
            email: this.email
        }, options, settings);
    }
    tokenizeGooglePaySync(options) {
        this.logDebug('About to create Yum token');
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        return tokenizeGooglePay({
            customerId: this.customer_id,
            customerType: this.customer_type,
            sessionId: this.session_id,
            email: this.email
        }, options);
    }
    tokenizeGooglePay(options) {
        var _this = this;
        return _async_to_generator$1(function*() {
            var _this_config_processorType;
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            if (((_this_config_processorType = _this.config.processorType) === null || _this_config_processorType === void 0 ? void 0 : _this_config_processorType.toLowerCase()) === 'cybersource' && _this.isConsumerAuthenticationEnabled()) {
                if (shouldStepUpGooglePay(options)) {
                    if (!options.setupPaymentFraudProtection) {
                        throw new Error('options.setupPaymentFraudProtection is required for Cybersource');
                    }
                    _this.logDebug('About to run DDC');
                    const { runDeviceDataCollection } = yield import('./cybersource.mjs');
                    yield runDeviceDataCollection(options.setupPaymentFraudProtection, _this.logger);
                }
            }
            _this.logDebug('About to create Yum token');
            return tokenizeGooglePay({
                customerId: _this.customer_id,
                customerType: _this.customer_type,
                sessionId: _this.session_id,
                email: _this.email
            }, options);
        })();
    }
    tokenizeBraintree(options) {
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        return tokenizeBraintree({
            customerId: this.customer_id,
            customerType: this.customer_type,
            sessionId: this.session_id,
            email: this.email
        }, options);
    }
    tokenizeGiftCard(giftCardData) {
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        return tokenizeGiftCard(this.customer_id, this.customer_type, this.session_id, this.email || giftCardData.email || '', giftCardData, this.config.processorType);
    }
    tokenizeSavedPayment(savedPaymentData) {
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        return tokenizeSavedPayment(this.customer_id, this.customer_type, this.session_id, this.email || savedPaymentData.email || '', savedPaymentData);
    }
    getApplePayMerchantSession() {
        if (!this.config) {
            throw new Error('Payment session configuration not found');
        }
        if (!this.config.applePay) {
            throw new Error('Apple Pay Merchant Verification session not found');
        }
        return this.config.applePay;
    }
    getBraintreeClientToken() {
        if (!this.config) {
            throw new Error('Payment session configuration not found');
        }
        if (!this.config.braintree) {
            throw new Error('Braintree Merchant session not found');
        }
        return this.config.braintree;
    }
    submitHostedPage() {
        const config = this.config;
        const processor = getProcessor(config.processorType);
        if (typeof processor.saveCard !== 'function') {
            throw new Error(`processor function is misconfigured for id '${config.processorType}'`);
        }
        if (this.form_is_valid === false) {
            throw new Error(`form is not in a valid state for submission`);
        }
        processor.saveCard();
    }
    stop() {
        const config = this.config;
        const processor = getProcessor(config.processorType);
        if (typeof processor.stop !== 'function') {
            throw new Error(`processor function is misconfigured for id '${config.processorType}'`);
        }
        processor.stop();
    }
    /**
   * Loads, initializes, and returns the payment processor SDK based on the configuration of the current
   * payment session
   */ // Note: the type signature is _technically_ incorrect, but a convenience to save the brand type-
    // casting the `sdk.initialize()` return value to the expected processor (as the return type is
    // a union of all processor SDKs)
    // A brand could call `sdk.initialize<CybersourceSDK>()`, but actually have a payment
    // session for Adyen, and so the return type would not match the expected - but the likelihood
    // of this actually happening is low
    // For reference, TS error without casting:
    // Typescript complains with
    //    Type 'Core' is not assignable to type 'T'.
    //    'Core' is assignable to the constraint of type 'T', but 'T' could be instantiated with a
    //    different subtype of constraint 'PaymentProcessorSDK'.
    initialize(options) {
        var _this = this;
        return _async_to_generator$1(function*() {
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const { processorType } = _this.config;
            if (processorType.toLowerCase().startsWith('cybersource')) {
                const { initialize } = yield import('./cybersource.mjs');
                return initialize({
                    logger: _this.logger
                }, {
                    config: _this.config
                });
            }
            if (processorType.toLowerCase().startsWith('adyen')) {
                const { initialize } = yield Promise.resolve().then(function () { return adyen; });
                return initialize({
                    logger: _this.logger
                }, {
                    config: _object_spread$1({}, _this.config),
                    options: _object_spread$1({}, options)
                });
            }
            throw new UnsupportedMethodError({
                processor: processorType
            });
        })();
    }
    /**
   * Formats a payment token, name and optional billing address into the expected
   * payment method format for Yum! Commerce Payments authorization requests
   */ tokenize(options) {
        var _this = this;
        return _async_to_generator$1(function*() {
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const processorType = options.paymentProcessorOverride || _this.config.processorType;
            if (processorType.toLowerCase().startsWith('cybersource')) {
                const { tokenize, runDeviceDataCollection } = yield import('./cybersource.mjs');
                if (_this.isConsumerAuthenticationEnabled()) {
                    _this.logDebug('About to run DDC');
                    const { setupPaymentFraudProtection } = options;
                    if (!setupPaymentFraudProtection) {
                        throw new TypeError('options.setupPaymentFraudProtection is required');
                    }
                    yield runDeviceDataCollection(setupPaymentFraudProtection, _this.logger);
                }
                _this.logDebug('About to tokenize');
                const config = _this.getExtendedConfig();
                return tokenize(_object_spread$1({
                    config
                }, options));
            }
            if (processorType.toLowerCase().startsWith('adyen')) {
                _this.logDebug('About to tokenize');
                const { tokenize } = yield Promise.resolve().then(function () { return adyen; });
                const config = _this.getExtendedConfig();
                return tokenize(_object_spread$1({
                    config
                }, options));
            }
            if (processorType.toLowerCase().startsWith('knet')) {
                _this.logDebug('About to tokenize');
                const { tokenize } = yield Promise.resolve().then(function () { return knet; });
                return tokenize(_object_spread$1({
                    config: _this.getExtendedConfig()
                }, options || {}));
            }
            if (processorType.toLowerCase().startsWith('evertec')) {
                _this.logDebug('About to tokenize');
                const { tokenize } = yield Promise.resolve().then(function () { return evertec; });
                return tokenize(_object_spread$1({
                    config: _this.getExtendedConfig()
                }, options || {}));
            }
            throw new UnsupportedMethodError({
                processor: processorType
            });
        })();
    }
    handlePendingAuthorization(options) {
        handlePendingAuthorization({
            logger: this.logger,
            config: this.config
        }, options);
    }
    hasSeenPendingAuthorizationChallenge(options) {
        hasSeenPendingAuthorizationChallenge({
            logger: this.logger,
            config: this.config
        }, options);
    }
    markPendingAuthorizationChallengeAsSeen(options) {
        markPendingAuthorizationChallengeAsSeen({
            logger: this.logger,
            config: this.config
        }, options);
    }
    discardPendingAuthorizationChallengeSeenMark(options) {
        discardPendingAuthorizationChallengeSeenMark({
            logger: this.logger,
            config: this.config
        }, options);
    }
    constructor(options){
        _define_property$2(this, "session_id", null);
        _define_property$2(this, "customer_id", '');
        _define_property$2(this, "customer_type", 'guest');
        _define_property$2(this, "debug", false);
        _define_property$2(this, "form_is_valid", true);
        _define_property$2(this, "org_id", 'sandbox');
        _define_property$2(this, "logger", void 0);
        _define_property$2(this, "email", '');
        _define_property$2(this, "config", {
            processorType: 'FDM',
            orgId: this.org_id,
            sessionId: this.session_id,
            customerId: this.customer_id,
            customerType: this.customer_type,
            email: this.email,
            isGuest: true,
            deviceId: ''
        });
        /**
   * @deprecated Use submitHostedPage()
   */ _define_property$2(this, "saveCard", ()=>{
            return this.submitHostedPage();
        });
        this.logger = new Logger(this.debug);
        try {
            var _this_config, _this_config1, _this_config2, _this_config3;
            this.config = options.session_config ? JSON.parse(base64Decode(options.session_config)) : this.config;
            this.session_id = options.session_id || '';
            this.customer_id = ((_this_config = this.config) === null || _this_config === void 0 ? void 0 : _this_config.customerId) || this.customer_id;
            this.customer_type = !((_this_config1 = this.config) === null || _this_config1 === void 0 ? void 0 : _this_config1.isGuest) ? 'registered' : 'guest';
            this.org_id = ((_this_config2 = this.config) === null || _this_config2 === void 0 ? void 0 : _this_config2.orgId) || this.org_id;
            this.email = ((_this_config3 = this.config) === null || _this_config3 === void 0 ? void 0 : _this_config3.email) || '';
            var _options_debug;
            this.debug = (_options_debug = options.debug) !== null && _options_debug !== void 0 ? _options_debug : this.debug;
        } catch (ex) {
            this.logger.error('could not parse session config');
            throw ex;
        }
    }
}

const giftCardBalanceByCardNumber = (cardNumber, securityCode)=>`query giftCardBalanceByCardNumber {
    giftCardBalanceByCardNumber (cardNumber: "${cardNumber}"${securityCode ? `, securityCode: "${securityCode}"` : ''}) {
        currentBalance {
            amount
            currencyCode
        }
    }
}`;

function _define_property$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again later or contact support.';
class ErrorWithReasonCode extends Error {
    constructor(message, reasonCode){
        super(message);
        _define_property$1(this, "reasonCode", void 0);
        this.reasonCode = reasonCode;
    }
}
const generateErrorMessageSubject = (reasonCodeParts)=>{
    if (!reasonCodeParts || reasonCodeParts.length === 0) {
        return 'details';
    }
    return reasonCodeParts.map((part)=>part.toLowerCase()).join(' ');
};
const generateErrorMessagePrefix = (prefix)=>{
    const upperCasePrefix = prefix || 'unknown'.toUpperCase();
    const prefixPhrases = {
        VAULT: 'Failed to save payment method',
        UNKNOWN: 'An unknown operation failed'
    };
    return prefixPhrases[upperCasePrefix] || `An operation related to '${upperCasePrefix.toLowerCase()}' failed`;
};
const generateErrorMessageContent = (reasonCodeParts)=>{
    if (!reasonCodeParts || reasonCodeParts.length === 0) {
        return GENERIC_ERROR_MESSAGE;
    }
    const [firstKeyword, ...subjectParts] = reasonCodeParts;
    const errorMessageSubject = generateErrorMessageSubject(subjectParts);
    const keywordHandlers = {
        INVALID: (subject)=>`the provided ${subject} appear to be invalid. Please check the information entered`,
        EXPIRED: (subject)=>`the ${subject} has expired. Please use a current one`,
        UNRECOGNIZED: (subject)=>`the ${subject} is not recognized or supported by our system`,
        UNSUPPORTED: (subject)=>`the ${subject} is not supported for this operation`,
        DUPLICATE: (subject)=>`a duplicate ${subject} was detected. It may already be saved or processed`,
        FAILED: (subject)=>`the operation failed during ${subject}`,
        ACCOUNT_LIMIT_REACHED: (subject)=>`the limit for ${subject} has been reached`,
        SECURITY: (_subject)=>`a security issue occurred`,
        VERIFICATION_REQUIRED: (_subject)=>`additional verification is required`,
        PROCESSOR_UNAVAILABLE: (_subject)=>`the payment processor is currently unavailable. Please try again shortly`,
        UNEXPECTED_ERROR: (_subject)=>`an unexpected error occurred. Please check the details or try again`
    };
    const multiKeyword = `${firstKeyword}_${subjectParts.join('_')}`;
    if (keywordHandlers[multiKeyword]) {
        return keywordHandlers[multiKeyword](errorMessageSubject);
    }
    const singleKeyword = firstKeyword;
    if (keywordHandlers[singleKeyword]) {
        return keywordHandlers[singleKeyword](errorMessageSubject);
    }
    // If no specific handler, revert to the simpler joining method
    return generateErrorMessageSubject(reasonCodeParts);
};
const parseErrorReasonCode = (error)=>{
    if (!error.reasonCode) {
        return new Error(GENERIC_ERROR_MESSAGE);
    }
    const [prefix, ...restReasonCodeParts] = error.reasonCode.split('_');
    const title = generateErrorMessagePrefix(prefix);
    const content = generateErrorMessageContent(restReasonCodeParts);
    return new Error(`${title}: ${content}.`);
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
class YCCommercePayments extends YCCommercePaymentsAdmin {
    startSession(options) {
        var _window_localStorage, _window, _window_navigator, _window1, _window_navigator1, _window2, _window_navigator2, _window3, _window_navigator3, _window4, _options_amount, _options, _phoneNumber;
        const localStorageDeviceId = ((_window = window) === null || _window === void 0 ? void 0 : (_window_localStorage = _window.localStorage) === null || _window_localStorage === void 0 ? void 0 : _window_localStorage.getItem('YUM_PAYMENTS_DEVICE_ID')) || null;
        const paymentMethods = options.payment_methods || [
            'BANK_CARD'
        ];
        const deviceId = options.device_id || localStorageDeviceId || v4();
        const userAgent = options.user_agent || ((_window1 = window) === null || _window1 === void 0 ? void 0 : (_window_navigator = _window1.navigator) === null || _window_navigator === void 0 ? void 0 : _window_navigator.userAgent) || '';
        const vendor = options.vendor || ((_window2 = window) === null || _window2 === void 0 ? void 0 : (_window_navigator1 = _window2.navigator) === null || _window_navigator1 === void 0 ? void 0 : _window_navigator1.vendor) || '';
        const language = options.language || ((_window3 = window) === null || _window3 === void 0 ? void 0 : (_window_navigator2 = _window3.navigator) === null || _window_navigator2 === void 0 ? void 0 : _window_navigator2.language) || 'en';
        const platform = options.platform || ((_window4 = window) === null || _window4 === void 0 ? void 0 : (_window_navigator3 = _window4.navigator) === null || _window_navigator3 === void 0 ? void 0 : _window_navigator3.platform) || '';
        const storeNumber = options.store_number || undefined;
        const email = options.email;
        let phoneNumber = options.phone_number;
        const firstName = options.first_name;
        const lastName = options.last_name;
        const appType = options.app_type || '';
        const onSuccess = options.onSuccess || (()=>null);
        const onError = options.onError || (()=>null);
        let amount;
        if ((_options = options) === null || _options === void 0 ? void 0 : (_options_amount = _options.amount) === null || _options_amount === void 0 ? void 0 : _options_amount.amount) {
            amount = {
                amount: options.amount.amount,
                currencyCode: options.amount.currency_code
            };
        }
        if (!((_phoneNumber = phoneNumber) === null || _phoneNumber === void 0 ? void 0 : _phoneNumber.match(/^\+[1-9]\d{6,14}$/)) || phoneNumber === '') {
            this.logDebug(`Submitted phoneNumber: ${phoneNumber} is not valid. Must match format as specified in: https://en.wikipedia.org/wiki/E.164. Removing from input`);
            phoneNumber = undefined;
        }
        /* Device fingerprinting for fraud detect */ if (localStorage) {
            localStorage.setItem('YUM_PAYMENTS_DEVICE_ID', deviceId);
        }
        this.email = email || this.email;
        this.beginPaymentSession({
            paymentMethods,
            deviceId,
            userAgent,
            vendor,
            language,
            platform,
            storeNumber,
            email,
            phoneNumber,
            firstName,
            lastName,
            appType,
            amount,
            onError,
            onSuccess
        }).then((res)=>{
            var _res_data, _res, _beginPaymentSession_session, _beginPaymentSession_session_device, _beginPaymentSession_session1;
            this.logDebug('response', res);
            if (!((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : _res_data.beginPaymentSession) || res.data.beginPaymentSession.__typename !== 'BeginPaymentSessionSuccess') {
                return onError(new Error('failed to start payment session'));
            }
            const beginPaymentSession = res.data.beginPaymentSession;
            const config = JSON.parse(base64Decode(beginPaymentSession.sessionConfig));
            this.config = config;
            this.session_id = ((_beginPaymentSession_session = beginPaymentSession.session) === null || _beginPaymentSession_session === void 0 ? void 0 : _beginPaymentSession_session.sessionId) || beginPaymentSession.sessionToken;
            this.device_id = (_beginPaymentSession_session1 = beginPaymentSession.session) === null || _beginPaymentSession_session1 === void 0 ? void 0 : (_beginPaymentSession_session_device = _beginPaymentSession_session1.device) === null || _beginPaymentSession_session_device === void 0 ? void 0 : _beginPaymentSession_session_device.deviceId;
            this._getDeviceDetails();
            onSuccess();
        }).catch((ex)=>{
            this.logger.error(ex);
            onError(ex);
        });
    }
    updateSession(options) {
        var _phoneNumber;
        if (!options) {
            throw new Error('No update options provided');
        }
        const deviceId = options.device_id || this.device_id;
        const userAgent = options.user_agent;
        const vendor = options.vendor;
        const language = options.language;
        const platform = options.platform;
        const email = options.email;
        let phoneNumber = options.phone_number;
        const firstName = options.first_name;
        const lastName = options.last_name;
        const onSuccess = options.onSuccess || (()=>null);
        const onError = options.onError || (()=>null);
        if (!((_phoneNumber = phoneNumber) === null || _phoneNumber === void 0 ? void 0 : _phoneNumber.match(/^\+[1-9]\d{6,14}$/)) || phoneNumber === '') {
            this.logDebug(`Submitted phoneNumber: ${phoneNumber} is not valid. Must match format as specified in: https://en.wikipedia.org/wiki/E.164. Removing from input`);
            phoneNumber = undefined;
        }
        this.email = email || this.email;
        const payload = {
            query: updatePaymentSession,
            variables: {
                input: {
                    sessionId: this.session_id,
                    customer: {
                        isGuest: this.customer_type === 'guest',
                        email,
                        phoneNumber,
                        firstName,
                        lastName
                    },
                    device: {
                        deviceId,
                        userAgent,
                        vendor,
                        language,
                        platform,
                        model: 'web',
                        sdkVersion: `${packageJson.name}:${packageJson.version}`
                    }
                }
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>onSuccess(res)).catch((err)=>onError(err));
    }
    captureCard(options) {
        var _options;
        const persistentToken = ((_options = options) === null || _options === void 0 ? void 0 : _options.create_persistent_token) || false;
        const originalOnSuccess = options.onSuccess;
        if (persistentToken) {
            options.onSuccess = (tender)=>{
                this.logDebug('commerce tender object', tender);
                this.createPersistentToken(tender, {
                    onSuccess: originalOnSuccess,
                    onError: options.onError
                });
            };
        }
        return this.executeCaptureCard(options);
    }
    beginPaymentSession(options) {
        const { paymentMethods, deviceId, userAgent, vendor, language, platform, storeNumber, email, appType, amount, phoneNumber, firstName, lastName, onError } = options;
        const payload = {
            query: beginPaymentSession,
            variables: {
                input: {
                    appType,
                    paymentMethods,
                    storeNumber,
                    customer: {
                        isGuest: this.customer_type === 'guest',
                        email,
                        phoneNumber,
                        firstName,
                        lastName
                    },
                    device: {
                        deviceId,
                        userAgent,
                        vendor,
                        language,
                        platform,
                        model: 'web',
                        sdkVersion: `${packageJson.name}:${packageJson.version}`
                    },
                    amount
                }
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                return onError(new Error(`Error response from server ${res.status}`));
            }
            return res.json();
        }).catch((err)=>onError(err));
    }
    createPersistentToken(tender, options) {
        var _tender;
        const { onSuccess, onError } = options;
        const payload = {
            query: createPaymentTokenMutation,
            variables: {
                input: {
                    payload: (_tender = tender) === null || _tender === void 0 ? void 0 : _tender.payment_method
                }
            }
        };
        const handleRefreshOnError = (err)=>{
            var _this_config, _processor;
            this.logger.error('Failed to tokenize', err);
            this.logDebug('Attempting to refresh session');
            const processor = getProcessor((_this_config = this.config) === null || _this_config === void 0 ? void 0 : _this_config.processorType);
            if (typeof ((_processor = processor) === null || _processor === void 0 ? void 0 : _processor.refresh) === 'function') {
                this._refreshPaymentSession({
                    onError
                }).then((res)=>{
                    var _res;
                    this.config = JSON.parse(base64Decode((_res = res) === null || _res === void 0 ? void 0 : _res.sessionConfig) || '');
                    try {
                        var _processor_refresh, _processor;
                        (_processor_refresh = (_processor = processor).refresh) === null || _processor_refresh === void 0 ? void 0 : _processor_refresh.call(_processor, this.config);
                        onError(new RetryableError('Tokenization failure. Session has been refreshed', {
                            cause: err.message
                        }));
                    } catch (err) {
                        onError(err);
                    }
                }).catch((err)=>onError(err));
            } else {
                this.logger.error('Cannot refresh session');
                onError(err);
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                return handleRefreshOnError(new Error(`Error response from server ${res.status}`));
            }
            return res.json().then((token)=>{
                var _token_data_createPaymentToken, _token_data, _token, _token_data_createPaymentToken1, _token_data1, _token1;
                let paymentToken;
                if ((_token = token) === null || _token === void 0 ? void 0 : (_token_data = _token.data) === null || _token_data === void 0 ? void 0 : (_token_data_createPaymentToken = _token_data.createPaymentToken) === null || _token_data_createPaymentToken === void 0 ? void 0 : _token_data_createPaymentToken.paymentToken) {
                    paymentToken = token.data.createPaymentToken.paymentToken;
                    onSuccess(_object_spread_props(_object_spread({}, tender), {
                        payment_method: paymentToken
                    }));
                } else if ((_token1 = token) === null || _token1 === void 0 ? void 0 : (_token_data1 = _token1.data) === null || _token_data1 === void 0 ? void 0 : (_token_data_createPaymentToken1 = _token_data1.createPaymentToken) === null || _token_data_createPaymentToken1 === void 0 ? void 0 : _token_data_createPaymentToken1.message) {
                    handleRefreshOnError(new Error(token.data.createPaymentToken.message));
                } else {
                    handleRefreshOnError(new Error(`Could not create payment token`));
                }
            });
        }).catch((err)=>handleRefreshOnError(err));
    }
    getSavedPaymentMethods(options) {
        const { onSuccess, onError, include_postal_code: includePostalCode, include_expiration_date: includeExpirationDate, include_prepaid_balance: includePrepaidBalance } = options;
        if (!this.session_id) {
            this._callOnError(new UninitializedSessionError(), onError);
        }
        if (this.customer_type !== 'registered') {
            this._callOnError(new Error('Saved payments are not available for guest users'), onError);
        }
        const payload = {
            query: savedPaymentMethodsQuery(includePostalCode, includeExpirationDate, includePrepaidBalance),
            variables: {}
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_savedPaymentMethods, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_savedPaymentMethods = _res_data.savedPaymentMethods) === null || _res_data_savedPaymentMethods === void 0 ? void 0 : _res_data_savedPaymentMethods.edges) {
                this._callOnSuccess(res.data.savedPaymentMethods.edges.filter((edge)=>!(this.config.allowedCardTypes && this.config.allowedCardTypes.length > 0 && edge.node.cardType && !this.config.allowedCardTypes.includes(edge.node.cardType))).map((edge)=>{
                    var _edge_node_balance_currentBalance, _edge_node_balance;
                    return {
                        payment_method_id: edge.node.paymentMethodId,
                        card_type: edge.node.cardType || 'GIFT_CARD',
                        last_four: edge.node.lastFour,
                        default: edge.node.default,
                        name: edge.node.name,
                        requires_security_code_by: edge.node.requiresSecurityCodeBy,
                        postal_code: edge.node.postalCode,
                        expiry_month: edge.node.expiryMonth,
                        expiry_year: edge.node.expiryYear,
                        balance: {
                            current_balance: {
                                amount: (_edge_node_balance = edge.node.balance) === null || _edge_node_balance === void 0 ? void 0 : (_edge_node_balance_currentBalance = _edge_node_balance.currentBalance) === null || _edge_node_balance_currentBalance === void 0 ? void 0 : _edge_node_balance_currentBalance.amount
                            }
                        }
                    };
                }), onSuccess);
                return;
            }
            this._callOnSuccess([], onSuccess);
        }).catch((error)=>this._callOnError(error, onError));
    }
    giftCardBalanceByCardNumber(options) {
        const { onSuccess, onError, card_number: cardNumber, security_code: securityCode } = options;
        const payload = {
            query: giftCardBalanceByCardNumber(cardNumber, securityCode),
            variables: {}
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_giftCardBalanceByCardNumber, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_giftCardBalanceByCardNumber = _res_data.giftCardBalanceByCardNumber) === null || _res_data_giftCardBalanceByCardNumber === void 0 ? void 0 : _res_data_giftCardBalanceByCardNumber.currentBalance) {
                this._callOnSuccess({
                    amount: res.data.giftCardBalanceByCardNumber.currentBalance.amount,
                    currency_code: res.data.giftCardBalanceByCardNumber.currentBalance.currencyCode
                }, onSuccess);
                return;
            }
            this._callOnError(new RetryableError('Gift card balance not found'), onError);
        }).catch((error)=>this._callOnError(error, onError));
    }
    createSavedPaymentMethod(options) {
        const { onSuccess, onError, payment_token: paymentToken, image_url: imageUrl, name } = options;
        if (!this.session_id) {
            this._callOnError(new UninitializedSessionError(), onError);
        }
        if (this.customer_type !== 'registered') {
            this._callOnError(new Error('Saved payments are not available for guest users'), onError);
        }
        const payload = {
            query: createSavedPaymentMethodMutation,
            variables: {
                input: {
                    name,
                    paymentToken,
                    imageUrl
                }
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_createSavedPaymentMethod, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_createSavedPaymentMethod = _res_data.createSavedPaymentMethod) === null || _res_data_createSavedPaymentMethod === void 0 ? void 0 : _res_data_createSavedPaymentMethod.paymentMethod) {
                this._callOnSuccess(res.data.createSavedPaymentMethod.paymentMethod, onSuccess);
                return;
            }
            this._callOnError(new ErrorWithReasonCode(res.data.createSavedPaymentMethod.message, res.data.createSavedPaymentMethod.reasonCode), onError);
        }).catch((error)=>this._callOnError(error, onError));
    }
    updateSavedPaymentMethod(options) {
        const { onSuccess, onError } = options;
        const paymentMethodId = options.payment_method_id;
        const name = options.name;
        const securityCode = options.security_code;
        const expiryMonth = options.expiry_month;
        const expiryYear = options.expiry_year;
        if (!this.session_id) {
            this._callOnError(new UninitializedSessionError(), onError);
        }
        if (this.customer_type !== 'registered') {
            this._callOnError(new Error('Saved payments are not available for guest users'), onError);
        }
        if (!paymentMethodId) {
            this._callOnError(new Error('payment_method_id must be specified'), onError);
        }
        const input = Object.assign({}, {
            paymentMethodId
        }, name ? {
            name
        } : null, securityCode ? {
            securityCode
        } : null, expiryMonth ? {
            expiryMonth
        } : null, expiryYear ? {
            expiryYear
        } : null);
        if (Object.keys(input).length === 1) {
            this._callOnError(new Error('no fields specified to update'), onError);
        }
        const payload = {
            query: updateSavedPaymentMethodMutation,
            variables: {
                input
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_updateSavedPaymentMethod, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_updateSavedPaymentMethod = _res_data.updateSavedPaymentMethod) === null || _res_data_updateSavedPaymentMethod === void 0 ? void 0 : _res_data_updateSavedPaymentMethod.paymentMethod) {
                this._callOnSuccess(res.data.updateSavedPaymentMethod.paymentMethod, onSuccess);
                return;
            }
            this._callOnError(new Error(res.data.updateSavedPaymentMethod.message), onError);
        }).catch((error)=>this._callOnError(error, onError));
    }
    setDefaultSavedPaymentMethod(options) {
        const { onSuccess, onError } = options;
        const paymentMethodId = options.payment_method_id;
        if (!this.session_id) {
            this._callOnError(new UninitializedSessionError(), onError);
        }
        if (this.customer_type !== 'registered') {
            this._callOnError(new Error('Saved payments are not available for guest users'), onError);
        }
        if (!paymentMethodId) {
            this._callOnError(new Error('payment_method_id must be specified'), onError);
        }
        const input = {
            paymentMethodId
        };
        const payload = {
            query: saveDefaultPaymentMethodMutation,
            variables: {
                input
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_setDefaultSavedPaymentMethod, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_setDefaultSavedPaymentMethod = _res_data.setDefaultSavedPaymentMethod) === null || _res_data_setDefaultSavedPaymentMethod === void 0 ? void 0 : _res_data_setDefaultSavedPaymentMethod.paymentMethod) {
                this._callOnSuccess(res.data.setDefaultSavedPaymentMethod.paymentMethod, onSuccess);
                return;
            }
            this._callOnError(new Error(res.data.setDefaultSavedPaymentMethod.message), onError);
        }).catch((error)=>this._callOnError(error, onError));
    }
    deleteSavedPaymentMethod(options) {
        const { onSuccess, onError } = options;
        const paymentMethodId = options.payment_method_id;
        if (!this.session_id) {
            this._callOnError(new UninitializedSessionError(), onError);
        }
        if (this.customer_type !== 'registered') {
            this._callOnError(new Error('Saved payments are not available for guest users'), onError);
        }
        if (!paymentMethodId) {
            this._callOnError(new Error('payment_method_id must be specified'), onError);
        }
        const input = {
            paymentMethodId
        };
        const payload = {
            query: deleteSavedPaymentMethodMutation,
            variables: {
                input
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_deleteSavedPaymentMethod, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_deleteSavedPaymentMethod = _res_data.deleteSavedPaymentMethod) === null || _res_data_deleteSavedPaymentMethod === void 0 ? void 0 : _res_data_deleteSavedPaymentMethod.paymentMethod) {
                this._callOnSuccess(res.data.deleteSavedPaymentMethod.paymentMethod, onSuccess);
                return;
            }
            this._callOnError(new ErrorWithReasonCode(res.data.deleteSavedPaymentMethod.message), onError);
        }).catch((error)=>this._callOnError(error, onError));
    }
    tokenizeGooglePay(options) {
        var _this = this;
        return _async_to_generator(function*() {
            var _this_config_processorType;
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            if (((_this_config_processorType = _this.config.processorType) === null || _this_config_processorType === void 0 ? void 0 : _this_config_processorType.toLowerCase()) === 'cybersource') {
                if (!isGooglePayPaymentData(options)) {
                    throw new Error('payment_method_data.tokenization_data.token is required for CYBERSOURCE');
                }
                if (shouldStepUpGooglePay(options) && _this.isConsumerAuthenticationEnabled()) {
                    const setupFraudResponse = yield _this.setupPaymentFraudProtection({
                        processorType: 'CYBERSOURCE',
                        cybersource: {
                            googlePayToken: options.payment_method_data.tokenization_data.token
                        }
                    });
                    if (!isSuccessfulSetupPaymentFraudProtectionResponse(setupFraudResponse)) {
                        _this.logger.error('setupPaymentFraudProtection failed', setupFraudResponse);
                        throw new Error('Failed to setup fraud protection');
                    }
                    const { data: { setupPaymentFraudProtection } } = setupFraudResponse;
                    _this.logDebug('About to run DDC');
                    const { runDeviceDataCollection } = yield import('./cybersource.mjs');
                    yield runDeviceDataCollection(setupPaymentFraudProtection, _this.logger);
                }
            }
            _this.logDebug('About to create Yum token');
            return tokenizeGooglePay({
                customerId: _this.customer_id,
                customerType: _this.customer_type,
                sessionId: _this.session_id,
                email: _this.email
            }, options);
        })();
    }
    /**
   * Loads, initializes, and returns the payment processor SDK based on the configuration of the current
   * payment session
   *
   */ // Note: the type signature is _technically_ incorrect, but a convenience to save the brand type-
    // casting the `sdk.initialize()` return value to the expected processor (as the return type is
    // a union of all processor SDKs)
    // A brand could call `sdk.initialize<CybersourceSDK>()`, but actually have a payment
    // session for Adyen, and so the return type would not match the expected - but the likelihood
    // of this actually happening is low
    // For reference, TS error without casting:
    // Typescript complains with
    //    Type 'Core' is not assignable to type 'T'.
    //    'Core' is assignable to the constraint of type 'T', but 'T' could be instantiated with a
    //    different subtype of constraint 'PaymentProcessorSDK'.
    initialize(options) {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const { processorType } = _this.config;
            if (processorType.toLowerCase().startsWith('cybersource')) {
                const { initialize } = yield import('./cybersource.mjs');
                return initialize({
                    logger: _this.logger
                }, {
                    config: _this.config
                });
            }
            if (processorType.toLowerCase().startsWith('adyen')) {
                const { initialize } = yield Promise.resolve().then(function () { return adyen; });
                return initialize({
                    logger: _this.logger
                }, {
                    config: _object_spread({}, _this.config),
                    options: _object_spread({}, options)
                });
            }
            throw new UnsupportedMethodError({
                processor: processorType
            });
        })();
    }
    /**
   * Formats a payment token, name and optional billing address into the expected
   * payment method format for Yum! Commerce Payments authorization requests
   */ tokenize(options) {
        var _this = this;
        return _async_to_generator(function*() {
            var _options;
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const processorType = ((_options = options) === null || _options === void 0 ? void 0 : _options.paymentProcessorOverride) || _this.config.processorType;
            if (processorType.toLowerCase().startsWith('cybersource')) {
                const { tokenize, runDeviceDataCollection } = yield import('./cybersource.mjs');
                if (_this.isConsumerAuthenticationEnabled()) {
                    const cybersourceOptions = options;
                    const setupFraudResponse = yield _this.setupPaymentFraudProtection({
                        processorType: 'CYBERSOURCE',
                        paymentMethodId: cybersourceOptions.tenderId,
                        cybersource: {
                            transientToken: cybersourceOptions.token
                        }
                    });
                    if (!isSuccessfulSetupPaymentFraudProtectionResponse(setupFraudResponse)) {
                        _this.logger.error('setupPaymentFraudProtection failed', setupFraudResponse);
                        throw new Error('Failed to setup fraud protection');
                    }
                    const { data: { setupPaymentFraudProtection } } = setupFraudResponse;
                    _this.logDebug('About to run DDC');
                    yield runDeviceDataCollection(setupPaymentFraudProtection, _this.logger);
                }
                _this.logDebug('About to tokenize');
                return tokenize(_object_spread({
                    config: _this.getExtendedConfig()
                }, options || {}));
            }
            if (processorType.toLowerCase().startsWith('adyen')) {
                const { tokenize } = yield Promise.resolve().then(function () { return adyen; });
                _this.logDebug('About to tokenize');
                return tokenize(_object_spread({
                    config: _this.getExtendedConfig()
                }, options || {}));
            }
            if (processorType.toLowerCase().startsWith('knet')) {
                const { tokenize } = yield Promise.resolve().then(function () { return knet; });
                _this.logDebug('About to tokenize');
                return tokenize(_object_spread({
                    config: _this.getExtendedConfig()
                }, options || {}));
            }
            if (processorType.toLowerCase().startsWith('evertec')) {
                const { tokenize } = yield Promise.resolve().then(function () { return evertec; });
                _this.logDebug('About to tokenize');
                return tokenize(_object_spread({
                    config: _this.getExtendedConfig()
                }, options || {}));
            }
            throw new UnsupportedMethodError({
                processor: processorType
            });
        })();
    }
    setupPaymentFraudProtection(processorInput) {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const { processorType } = _this.config;
            if (processorType.toLowerCase() !== 'cybersource') {
                throw new UnsupportedMethodError({
                    processor: processorType
                });
            }
            const payload = {
                query: setupPaymentFraudProtection,
                variables: {
                    input: {
                        sessionToken: _this.session_id,
                        processor: processorInput
                    }
                }
            };
            _this.logDebug(_this.base_url, payload);
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${_this.access_token}`
            };
            return fetch(_this.base_url, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers
            }).then((res)=>{
                if (!(res.status >= 200 && res.status < 300)) {
                    throw new Error(`Error response from server ${res.status}`);
                }
                return res.json();
            });
        })();
    }
    handlePendingAuthorization(options) {
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        const updatedOptions = _object_spread_props(_object_spread({}, options), {
            sessionId: this.session_id,
            // Wrap error callback adding our own error handler which is going to call the force pending auth resolution mutation
            onError: (error)=>this.forcePendingAuthorizationResolution().then(()=>{
                    var _options_onError, _options;
                    return (_options_onError = (_options = options).onError) === null || _options_onError === void 0 ? void 0 : _options_onError.call(_options, error);
                })
        });
        handlePendingAuthorization({
            logger: this.logger,
            config: this.config
        }, updatedOptions);
    }
    forcePendingAuthorizationResolution() {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const { processorType } = _this.config;
            if (processorType.toLowerCase() !== 'cybersource') {
                throw new UnsupportedMethodError({
                    processor: processorType
                });
            }
            const payload = {
                query: forcePendingAuthorizationResolution,
                variables: {
                    input: {
                        sessionId: _this.session_id
                    }
                }
            };
            _this.logDebug(_this.base_url, payload);
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${_this.access_token}`
            };
            return fetch(_this.base_url, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers
            }).then((res)=>{
                if (!(res.status >= 200 && res.status < 300)) {
                    throw new Error(`Error response from server ${res.status}`);
                }
                return res.json();
            });
        })();
    }
    notifyPendingAuthorization(authorizationId, options) {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.session_id) {
                throw new UninitializedSessionError();
            }
            const { processorType } = _this.config;
            // For now we are only allowing adyen to perform notifications thru the SDK even tho any processor can use this
            if (!processorType.toLowerCase().startsWith('adyen')) {
                throw new UnsupportedMethodError({
                    processor: processorType
                });
            }
            const { returnUrl, data } = options;
            const graphqlPath = '/graphql';
            const storefrontPath = '/storefront';
            const regex = new RegExp(`${graphqlPath}|${storefrontPath}`, 'g');
            const headers = {
                'Content-Type': 'application/json'
            };
            const query = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
            const url = `${_this.base_url.replace(regex, '')}/webhooks/v1/pending-authorization/${authorizationId}/notify${query}`;
            _this.logDebug(url, data);
            yield fetch(url, {
                method: 'POST',
                body: JSON.stringify(data),
                headers
            }).then(function() {
                var _ref = _async_to_generator(function*(res) {
                    if (!(res.status >= 200 && res.status < 300)) {
                        throw new Error(`Error response from server ${res.status}`);
                    }
                    if (res.redirected) {
                        window.location.href = res.url;
                        return;
                    }
                    const contentType = res.headers.get('Content-Type');
                    if (contentType && contentType.includes('text/html')) {
                        return res.text();
                    }
                    return res.json();
                });
                return function(res) {
                    return _ref.apply(this, arguments);
                };
            }());
        })();
    }
    _getDeviceDetails() {
        if (!this.session_id) {
            throw new UninitializedSessionError();
        }
        const { processorType } = this.config;
        let processor = {};
        try {
            processor = getProcessor(processorType);
        } catch (err) {
            this.logger.error(err);
        }
        if (Object.hasOwn(processor, 'initDeviceDataCollection')) {
            var _processor_initDeviceDataCollection, _processor;
            (_processor_initDeviceDataCollection = (_processor = processor).initDeviceDataCollection) === null || _processor_initDeviceDataCollection === void 0 ? void 0 : _processor_initDeviceDataCollection.call(_processor, this.config.publicApiKey).then((deviceId)=>{
                this.device_id = deviceId;
                const input = {
                    sessionId: this.session_id,
                    customer: {
                        isGuest: this.customer_type.toLowerCase() !== 'registered'
                    },
                    device: {
                        deviceId
                    }
                };
                const payload = {
                    query: updatePaymentSession,
                    variables: {
                        input
                    }
                };
                this.logDebug(this.base_url, payload);
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.access_token}`
                };
                return fetch(this.base_url, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                    headers
                }).then((res)=>{
                    if (!(res.status >= 200 && res.status < 300)) {
                        throw new Error(`Error response from server ${res.status}`);
                    }
                    return res.json();
                }).then((res)=>{
                    var _res_data_updatePaymentSession, _res_data, _res_data_updatePaymentSession1, _res_data1;
                    this.logDebug(this.base_url, res);
                    if ((_res_data = res.data) === null || _res_data === void 0 ? void 0 : (_res_data_updatePaymentSession = _res_data.updatePaymentSession) === null || _res_data_updatePaymentSession === void 0 ? void 0 : _res_data_updatePaymentSession.session) {
                        return;
                    }
                    this.logger.error(new Error((_res_data1 = res.data) === null || _res_data1 === void 0 ? void 0 : (_res_data_updatePaymentSession1 = _res_data1.updatePaymentSession) === null || _res_data_updatePaymentSession1 === void 0 ? void 0 : _res_data_updatePaymentSession1.message));
                }).catch((error)=>this.logger.error(error));
            }).catch((err)=>{
                this.logger.error(err);
            });
        } else {
            this.logDebug(`Processor ${processorType} has not implemented initDeviceDataCollection. Falling back to Yum device information`);
        }
    }
    _refreshPaymentSession(options) {
        const { onError } = options;
        const input = {
            sessionId: this.session_id
        };
        const payload = {
            query: refreshPaymentSessionMutation,
            variables: {
                input
            }
        };
        this.logDebug(this.base_url, payload);
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.access_token}`
        };
        return fetch(this.base_url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers
        }).then((res)=>{
            if (!(res.status >= 200 && res.status < 300)) {
                throw new Error(`Error response from server ${res.status}`);
            }
            return res.json();
        }).then((res)=>{
            var _res_data_refreshPaymentSession, _res_data, _res;
            this.logDebug(this.base_url, res);
            if ((_res = res) === null || _res === void 0 ? void 0 : (_res_data = _res.data) === null || _res_data === void 0 ? void 0 : (_res_data_refreshPaymentSession = _res_data.refreshPaymentSession) === null || _res_data_refreshPaymentSession === void 0 ? void 0 : _res_data_refreshPaymentSession.sessionConfig) {
                var _res_data1, _res1;
                return (_res1 = res) === null || _res1 === void 0 ? void 0 : (_res_data1 = _res1.data) === null || _res_data1 === void 0 ? void 0 : _res_data1.refreshPaymentSession;
            }
            this._callOnError(new Error(res.data.refreshPaymentSession.message), onError);
        }).catch((error)=>this._callOnError(error, onError));
    }
    constructor(options){
        super(options);
        _define_property(this, "access_token", void 0);
        _define_property(this, "base_url", void 0);
        _define_property(this, "environment", void 0);
        _define_property(this, "device_id", void 0);
        _define_property(this, "_callOnSuccess", (input, onSuccess)=>{
            if (typeof onSuccess === 'function') {
                onSuccess(input);
            }
        });
        _define_property(this, "_callOnError", (error, onError)=>{
            this.logDebug(error);
            if (typeof onError === 'function' && !error.reasonCode) {
                onError(error);
                return;
            }
            if (typeof onError === 'function' && error.reasonCode) {
                onError(parseErrorReasonCode(error));
                return;
            }
            throw error;
        });
        const { api_url_override: apiUrlOverride, org_id: orgId, environment, access_token: accessToken } = options;
        if (apiUrlOverride) {
            this.base_url = apiUrlOverride;
        } else {
            switch(environment.toLowerCase()){
                case 'production':
                    this.base_url = `https://api.prod.yumconnect.dev/${orgId}/storefront/graphql`;
                    break;
                case 'qa':
                    this.base_url = `https://api.qa.yumconnect.dev/${orgId}/storefront/graphql`;
                    break;
                case 'dev':
                    this.base_url = `https://api.dev.yumconnect.dev/${orgId}/storefront/graphql`;
                    break;
                default:
                    this.base_url = `https://api.staging.yumconnect.dev/${orgId}/storefront/graphql`;
                    break;
            }
        }
        if (!accessToken) {
            throw new Error('Missing required constructor value: access_token');
        }
        try {
            var _payload_ext;
            const payload = JSON.parse(base64Decode(accessToken.replace(/bearer\s/gi, '').split('.')[1]));
            this.customer_id = payload.sub || payload.customer_id || '';
            this.customer_type = payload.is_guest || ((_payload_ext = payload.ext) === null || _payload_ext === void 0 ? void 0 : _payload_ext.is_guest) ? 'guest' : 'registered';
        } catch (ex) {
            this.logger.error('could not parse bearer token');
            throw ex;
        }
        this.environment = environment;
        this.access_token = accessToken.replace(/bearer\s/gi, '');
        this.org_id = orgId;
        this.form_is_valid = true;
        this.logger = new Logger(this.debug);
    }
}

export { BaseError, ConfigError, DuplicatePendingAuthorizationHandleError, HTMLElementNotFoundError, ProcessorLoadError, ProcessorTimeoutError, SessionExpiredError, UninitializedSessionError, UnsupportedMethodError, YCCommercePayments, YCCommercePaymentsAdmin, base64Decode, base64Encode, productTypeMap };
//# sourceMappingURL=bundle.mjs.map
